import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URLS } from '../../config';
import { apiRequest } from '../../utils/apiRequest';
import { DEFAULT_CURRENCY_RATES, fetchCurrencyRateHistory, ratesAsOf, rateFromHistoryAsOf } from '../../utils/currencyRates';
import { useToast } from '../../Components/Toast';
import { monthlyShareAmount } from '../../utils/tentativeDates';
import DatePicker from 'react-multi-date-picker';
import Select from 'react-select';
import {
  FiPlus, FiEdit, FiTrash2, FiEye, FiCheck, FiX,
  FiSearch, FiRefreshCw, FiDownload, FiBell,
  FiDollarSign, FiInfo,
  FiFileText, FiChevronUp, FiChevronDown, FiCalendar, FiClock,
} from 'react-icons/fi';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import FilterPanel, { FilterField, filterSelectStyles } from '../../Components/FilterPanel';
import './Finance.css';

// Default for the header's invoice-month filter — "YYYY-MM" for the current month.
const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Shift-click range selection for the Overtime date pickers — mirrors the
// "Tentative Dates" picker in src/pages/Manday.jsx exactly (same helpers, same
// UX: hold Shift and click a second date to select every day in between).
const expandDateRange = (startStr, endStr) => {
  const [a, b] = [new Date(startStr), new Date(endStr)].sort((x, y) => x - y);
  const out = [];
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

const makeShiftRangeMapDays = ({ getValues, setValues, anchorRef }) =>
  ({ date }) => {
    const dateStr = date.format('YYYY-MM-DD');
    return {
      style: { userSelect: 'none', WebkitUserSelect: 'none' },
      onMouseDown: (e) => { if (e.shiftKey) e.preventDefault(); },
      onClick: (e) => {
        if (e.shiftKey && anchorRef.current) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof window !== 'undefined' && window.getSelection) {
            window.getSelection().removeAllRanges();
          }
          const range = expandDateRange(anchorRef.current, dateStr);
          const merged = Array.from(new Set([...(getValues() || []), ...range])).sort();
          setValues(merged);
        } else {
          anchorRef.current = dateStr;
        }
      },
    };
  };

const EMPTY_FORM = {
  invoiceNumber: '',
  invoiceDate: new Date().toISOString().split('T')[0],
  dueDate: '',
  jobNumber: '', customerName: '', customerId: '', poNumber: '', remarks: '',
  amount: '', otherCosts: [], includeOtherCostsInTax: false, taxes: [],
  total: '', invoiceCurrency: 'INR',
  enquiryId: '', lineItems: [],
  enquiryIds: [], jobNumbers: [], notifIds: [],
};

function daysOf(li) { return li.numberOfDays || li.inspectionDates?.length || 0; }

// This line item's total in its own offer currency — trusts the server-computed
// totalOfferPrice (which correctly shares a Manmonth line item's monthly rate across
// however many days it actually covers that month, rather than naively multiplying a
// monthly rate by a raw day count) and only falls back to offered×days for legacy
// line items saved before that field existed.
function totalOfferOf(li) {
  return li.totalOfferPrice != null ? (parseFloat(li.totalOfferPrice) || 0) : (parseFloat(li.offered) || 0) * daysOf(li);
}

// Line item fields to update when Finance picks which inspection dates this invoice
// bills. A Manmonth line item re-shares its monthly rate over the picked days with the
// same divisor computeNewLineItems uses (backend/utils/invoiceLineItems.js), instead of
// a flat offered×days.
function lineItemDateUpdates(li, dates) {
  const sorted = [...dates].sort();
  const totalOfferPrice = li.enquiryType === 'MANMONTH'
    ? monthlyShareAmount(sorted, li.assignedDates || li.availableDates || sorted, li.offered, li.workingDaysByMonth || {})
    : (parseFloat(li.offered) || 0) * sorted.length;
  return { inspectionDates: sorted, numberOfDays: sorted.length, totalOfferPrice };
}

// Same total, converted to INR — scales totalOfferOf by whatever offered→INR ratio
// offeredINR/offered implies, rather than day-multiplying the per-unit offeredINR
// rate (which would overstate a Manmonth line item's INR total the same way a flat
// offered×days would).
function totalOfferedINROf(li) {
  const offered = parseFloat(li.offered) || 0;
  const offeredINR = parseFloat(li.offeredINR) || 0;
  if (!offered) return 0;
  return totalOfferOf(li) * (offeredINR / offered);
}

// Standard India GST state codes — mirrors GST_STATE_CODES in
// backend/utils/salesInvoiceHtml.js, so the Customer Info popup shows the exact same
// "Code" the PDF would print next to the customer's State.
const GST_STATE_CODES = {
  'jammu and kashmir': '01', 'himachal pradesh': '02', punjab: '03', chandigarh: '04',
  uttarakhand: '05', haryana: '06', delhi: '07', rajasthan: '08', 'uttar pradesh': '09',
  bihar: '10', sikkim: '11', 'arunachal pradesh': '12', nagaland: '13', manipur: '14',
  mizoram: '15', tripura: '16', meghalaya: '17', assam: '18', 'west bengal': '19',
  jharkhand: '20', odisha: '21', chhattisgarh: '22', 'madhya pradesh': '23', gujarat: '24',
  'daman and diu': '25', 'dadra and nagar haveli': '26', maharashtra: '27', karnataka: '29',
  goa: '30', lakshadweep: '31', kerala: '32', 'tamil nadu': '33', puducherry: '34',
  'andaman and nicobar islands': '35', telangana: '36', 'andhra pradesh': '37', ladakh: '38',
};

// Formats a YYYY-MM-DD (or any Date-parseable) string as dd-mm-yy for display —
// used for the read-only Overtime dates list in the View modal.
function fmtDDMMYY(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}-${mm}-${yy}`;
}

// Days remaining until (positive) or elapsed past (negative) a due date, counting
// through the end of the due date itself — matches effectiveReceivableStatus's
// end-of-day cutoff server-side, so "0 days" always means "due today", not overdue yet.
function daysFromDue(dueDate) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  due.setHours(23, 59, 59, 999);
  return Math.ceil((due.getTime() - Date.now()) / 86400000);
}

// Due Date cell content — black "N days left"/"Due today" while still due, red
// "N days overdue" once past it. Shows only the day count, not the date itself.
function DueDateDaysLabel({ dueDate }) {
  const days = daysFromDue(dueDate);
  if (days == null) return '—';
  if (days >= 0) {
    return (
      <span style={{ color: '#111827', fontWeight: 500 }}>
        {days === 0 ? 'Due today' : `${days} day${days === 1 ? '' : 's'} left`}
      </span>
    );
  }
  const overdueDays = Math.abs(days);
  return (
    <span style={{ color: '#dc2626', fontWeight: 700 }}>
      {overdueDays} day{overdueDays === 1 ? '' : 's'} overdue
    </span>
  );
}

// Not every currency has a widely-recognized single-glyph symbol (AED/QAR don't, so
// they stay as their code) — everything else shows its usual symbol instead of the code.
const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', EUR: '€', GBP: '£', SGD: 'S$', AUD: 'A$' };
const currencySymbol = (code) => CURRENCY_SYMBOLS[(code || 'INR').toUpperCase()] || (code || 'INR').toUpperCase();

// One overtime entry's amount, in the line item's own offer currency — hours × rate.
function overtimeEntryAmount(entry) {
  return (parseFloat(entry?.hours) || 0) * (parseFloat(entry?.rate) || 0);
}

// Sum of all overtime entries for one line item, in that line item's own offer
// currency (same currency as `offered`).
function overtimeAmountOf(li) {
  return (li.overtime || []).reduce((s, ot) => s + overtimeEntryAmount(ot), 0);
}

// The single currency shared by all line items, or INR when there are none (manual
// invoice) or line items are mixed-currency — used only as the suggested default when
// line items are first loaded. The invoice's actual billing currency (independent of
// each line item's own offer currency) is form.invoiceCurrency once that's set.
function getInvoiceCurrency(form) {
  const currencies = [...new Set((form.lineItems || []).map(li => (li.currencyOffered || 'INR').toUpperCase()))];
  return currencies.length === 1 ? currencies[0] : 'INR';
}

// The auto (non-overridden) invoicing rate for one line item under a given invoice
// currency — offered-currency's INR rate ÷ invoice-currency's INR rate. Works out to
// 1 when they're the same currency (e.g. USD offered, USD invoiced), the raw FX rate
// when one side is INR, and a proper cross-rate otherwise (e.g. INR offered, USD invoiced).
function defaultPaymentRate(li, invoiceCurrency, rates = DEFAULT_CURRENCY_RATES) {
  const offeredRate = rates[(li.currencyOffered || 'INR').toUpperCase()] ?? 1;
  const targetRate = rates[(invoiceCurrency || 'INR').toUpperCase()] ?? 1;
  return targetRate ? offeredRate / targetRate : offeredRate;
}

// Applies the sensible starting invoice currency (whatever the line items were offered
// in, when uniform) and resets each line item's invoicing rate to match — used whenever
// line items are freshly (re)loaded from an enquiry, so a fetch never carries forward a
// stale rate computed for a previously-selected invoice currency.
function withDefaultInvoiceCurrency(lineItems, rates) {
  const invoiceCurrency = getInvoiceCurrency({ lineItems });
  const nextLineItems = lineItems.map(li => ({ ...li, paymentRate: defaultPaymentRate(li, invoiceCurrency, rates) }));
  return { invoiceCurrency, lineItems: nextLineItems };
}

// Invoicing rate baseline — line-items total, or the manual Amount field when there are none.
function computeRawBase(form) {
  if (form.lineItems && form.lineItems.length > 0) {
    return form.lineItems.reduce((sum, li) => {
      const pRate = parseFloat(li.paymentRate ?? li.offerConvRate ?? 1) || 0;
      const dayAmt = totalOfferOf(li) * pRate;
      const otAmt = overtimeAmountOf(li) * pRate;
      return sum + dayAmt + otAmt;
    }, 0);
  }
  return parseFloat(form.amount) || 0;
}

// Sums otherCosts, converted into the invoice's own currency (each entry can carry a
// different currency than both the line items and the invoice itself).
function computeOtherCostsTotal(form, rates = DEFAULT_CURRENCY_RATES) {
  const invoiceCurrency = form.invoiceCurrency || getInvoiceCurrency(form);
  const targetRate = rates[invoiceCurrency] ?? 1;
  return (form.otherCosts || []).reduce((s, oc) => {
    const rate = rates[oc.currency] ?? 1;
    return s + ((parseFloat(oc.amount) || 0) * rate) / targetRate;
  }, 0);
}

// The base each "Add Tax" percentage is applied to — the invoicing amount (line-items
// total, or the manual Amount field) by default. Other costs join the taxable base
// too only when "Include Other Cost in Tax" is checked; otherwise they're added to
// the total afterward untaxed. Already in the invoice's own currency, since both the
// line items' paymentRate and other costs' own rate convert straight into it.
function computeTaxBase(form, rates = DEFAULT_CURRENCY_RATES) {
  const base = computeRawBase(form);
  return form.includeOtherCostsInTax ? base + computeOtherCostsTotal(form, rates) : base;
}

function calcTotal(form, rates = DEFAULT_CURRENCY_RATES) {
  const taxBase = computeTaxBase(form, rates);
  const other = computeOtherCostsTotal(form, rates);
  const taxesTotal = (form.taxes || []).reduce((s, t) => s + (taxBase * (parseFloat(t.percentage) || 0)) / 100, 0);
  return +(computeRawBase(form) + other + taxesTotal).toFixed(2);
}

// Recomputes each tax row's amount (base × percentage / 100 — taxBase is already in
// the invoice's own currency) and the overall total, so the amount that gets saved
// always matches what's displayed — not just the percentage.
function applyTotals(form, rates = DEFAULT_CURRENCY_RATES) {
  const taxBase = computeTaxBase(form, rates);
  const taxes = (form.taxes || []).map((t) => ({
    ...t,
    amount: +((taxBase * (parseFloat(t.percentage) || 0)) / 100).toFixed(2),
  }));
  const next = { ...form, taxes };
  next.total = calcTotal(next, rates);
  return next;
}

// Searchable customer filter for the header — a plain <select> doesn't scale once
// the customer master has hundreds of rows, so this pairs a search input with a
// scrollable option list in a popover, same footprint as the other .fin-select controls.
function CustomerFilterDropdown({ customers, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = customers.filter((c) => (c.customerName || '').toLowerCase().includes(query.toLowerCase()));
  const selectedId = selected?.id || selected?._id;

  const optionStyle = (active) => ({
    display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
    border: 'none', background: active ? '#eef1f8' : 'none', color: active ? '#1a3a6b' : '#333',
    fontWeight: active ? 600 : 400, fontSize: 13, cursor: 'pointer',
  });

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        type="button"
        className="fin-select"
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', maxWidth: 180 }}
        onClick={() => setOpen((o) => !o)}
        title="Filter by customer"
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.customerName : 'All Customers'}
        </span>
        <FiChevronDown size={12} style={{ flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)', width: 240, maxHeight: 320,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid #eee' }}>
            <input
              autoFocus
              className="fin-search-input"
              style={{ paddingLeft: 10 }}
              placeholder="Search customer…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div style={{ overflowY: 'auto' }}>
            <button type="button" style={optionStyle(!selected)} onClick={() => { onSelect(null); setOpen(false); setQuery(''); }}>
              All Customers
            </button>
            {filtered.map((c) => (
              <button
                key={c.id || c._id}
                type="button"
                style={optionStyle(selectedId === (c.id || c._id))}
                onClick={() => { onSelect(c); setOpen(false); setQuery(''); }}
              >
                {c.customerName}
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 10, fontSize: 12, color: '#999' }}>No customers found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Job Number cell — clickable through to the source enquiry in Manday.jsx, same
// "?from=" convention InspectionInvoice.jsx already uses so the Back button there
// knows to return to this page instead of the Enquiry list. Opens in a new tab so
// this page's own filters/scroll position aren't lost; a combined invoice spanning
// several jobs links each job number to its own enquiry (jobNumbers/enquiryIds are
// parallel arrays).
const JobNumberCell = ({ row }) => {
  const jobNumbers = row.jobNumbers?.length ? row.jobNumbers : (row.jobNumber ? [row.jobNumber] : []);
  if (jobNumbers.length === 0) return '—';
  return jobNumbers.map((jn, idx) => {
    const enquiryId = row.enquiryIds?.[idx] || (idx === 0 ? row.enquiryId : null);
    return (
      <React.Fragment key={`${jn}-${idx}`}>
        {idx > 0 && ', '}
        {enquiryId ? (
          <Link
            to={`/enquiry/manday/${enquiryId}?from=customer-invoice`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#437cf0', textDecoration: 'underline' }}
          >
            {jn}
          </Link>
        ) : jn}
      </React.Fragment>
    );
  });
};

export default function CustomerInvoice() {
  const { token, user } = useAuth();
  const toast = useToast();
  const isGlobalSuperAdmin = user?.role === 'global_superadmin';

  const [activeTab, setActiveTab] = useState('invoices');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Company filter — global_superadmin only, everyone else is already restricted
  // to their own company server-side (financeScopeFilter).
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState('');
  useEffect(() => {
    if (!isGlobalSuperAdmin) return;
    apiRequest(API_URLS.COMPANIES, {}, token).then((result) => {
      if (result?.ok) setCompanies(Array.isArray(result.data) ? result.data : (result.data?.rows || []));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalSuperAdmin, token]);
  // '' | 'asc' | 'desc' — same toggle pattern as Enquiry.jsx's Enquiry/Inspection Date sort
  const [invoiceNumberSortDir, setInvoiceNumberSortDir] = useState('');

  // Customer / Enquiry Type / Invoice Month filters — sit to the right of the
  // company filter in the header, same server-paginated filtering as above.
  const [customerOptions, setCustomerOptions] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState(null); // { id, customerName } | null
  const [enquiryTypeFilter, setEnquiryTypeFilter] = useState('');
  const [invoiceMonthFilter, setInvoiceMonthFilter] = useState(currentMonthKey);
  useEffect(() => {
    apiRequest(`${API_URLS.CUSTOMERS}?limit=1000`, {}, token).then((result) => {
      if (result?.ok) setCustomerOptions(result.data?.rows || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Coordinator options for the "Request to Customer Invoice" modal filter — same
  // /api/users?employeeType=coordinator lookup Enquiry.jsx uses for its own coordinator filter.
  const [coordinatorOptions, setCoordinatorOptions] = useState([]);
  useEffect(() => {
    apiRequest(`${API_URLS.USERS}?limit=1000`, {}, token).then((result) => {
      if (result?.ok) {
        const sorted = (result.data.rows || [])
          .filter((u) => u.employeeType === 'coordinator')
          .sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));
        setCoordinatorOptions(sorted);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // "Request to Customer Invoice" modal's own company/customer/coordinator filters —
  // independent of the invoice-list filters above, scoped only to the notifications fetch.
  const [notifCompanyFilter, setNotifCompanyFilter] = useState('');
  const [notifCustomerFilter, setNotifCustomerFilter] = useState(null); // { id, customerName } | null
  const [notifCoordinatorFilter, setNotifCoordinatorFilter] = useState('');
  const [notifSearch, setNotifSearch] = useState('');

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [selectedNotifIds, setSelectedNotifIds] = useState([]);
  const notifQuery = notifSearch.trim().toLowerCase();
  const filteredNotifications = notifQuery
    ? notifications.filter(n => [n.jobNumber, n.customerName, n.inspectorName]
      .some(v => String(v || '').toLowerCase().includes(notifQuery)))
    : notifications;

  // Modal
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [selected, setSelected] = useState(null);
  const [addJobPicker, setAddJobPicker] = useState(null); // from-job response awaiting date selection
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Per-invoice busy flags for the Preview/Download PDF buttons — PDF generation
  // (Puppeteer, server-side) takes a few seconds, so these drive a spinner + disabled
  // state on just the row/button that was clicked, keyed by invoice _id.
  const [previewingId, setPreviewingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // Customer Info popup — the info icon next to "Customer Name" on the invoice form.
  // Fetches the full Customer master record on demand (not preloaded) since this page
  // only otherwise carries customerId/customerName off the invoice itself.
  const [customerInfoModal, setCustomerInfoModal] = useState(null); // null | { loading, data, error }

  // Currency rates (DB-backed) — editing happens on the Currency Rate Master page,
  // this page only reads them for invoice conversion math. Date-aware: reflects the
  // rates as of the form's own Invoice Date (see the effect below), so Invoicing
  // Rate / Invoicing Rate (INR) auto-fetch by Invoice Date.
  const [currencyRates, setCurrencyRates] = useState(DEFAULT_CURRENCY_RATES);
  const [rateHistory, setRateHistory] = useState({});
  const prevCurrencyRatesRef = useRef(DEFAULT_CURRENCY_RATES); // previous currencyRates, to tell which line items were still on the old default vs. manually overridden
  const [showRatesPopup, setShowRatesPopup] = useState(false);

  // Receivables tab — its own server-paginated fetch (status=INVOICE_ISSUED), separate
  // from the Customer Invoice tab's `rows`/`page`/`total` above so each tab paginates
  // independently instead of the Receivables view being limited to whatever page of
  // the full invoice list happened to be loaded.
  const [receivableRows, setReceivableRows] = useState([]);
  const [receivableTotal, setReceivableTotal] = useState(0);
  const [receivablePage, setReceivablePage] = useState(1);
  const [receivableLoading, setReceivableLoading] = useState(true);
  const [receivableInvoiceNumberSortDir, setReceivableInvoiceNumberSortDir] = useState('');
  const [receivableSearch, setReceivableSearch] = useState('');

  // Receivables — "Receive Payment" bulk action (checkbox-select rows, then record
  // the amount/rate actually credited across all of them at once)
  const [selectedReceivableIds, setSelectedReceivableIds] = useState([]);
  const [showReceivePaymentModal, setShowReceivePaymentModal] = useState(false);
  // 'full' — receive the whole remaining balance of every selected invoice;
  // 'partial' — record part of one invoice's balance (exactly one row selected).
  const [receiveMode, setReceiveMode] = useState('full');
  const [receivePaymentForm, setReceivePaymentForm] = useState({ date: '', amount: '', tdsAmount: '', rate: '', bankCharges: '' });
  const [receiveRateTouched, setReceiveRateTouched] = useState(false); // stops auto-updating the rate from Payment Date once typed manually
  const [receivingPayment, setReceivingPayment] = useState(false);

  /* ── fetch invoices ─────────────────────────────────────── */
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    if (invoiceNumberSortDir) params.set('invoiceNumberSortDir', invoiceNumberSortDir);
    if (filterCompany && isGlobalSuperAdmin) params.set('company', filterCompany);
    if (filterCustomer) params.set('customerId', filterCustomer.id || filterCustomer._id);
    if (enquiryTypeFilter) params.set('enquiryType', enquiryTypeFilter);
    if (invoiceMonthFilter) params.set('invoiceMonth', invoiceMonthFilter);
    const result = await apiRequest(`${API_URLS.FINANCE_SALES_INVOICES}?${params}`, {}, token);
    if (result?.ok) {
      setRows(result.data.rows || []);
      setTotal(result.data.total || 0);
    }
    setLoading(false);
  }, [token, page, limit, search, statusFilter, invoiceNumberSortDir, filterCompany, isGlobalSuperAdmin, filterCustomer, enquiryTypeFilter, invoiceMonthFilter]);

  /* ── fetch receivables (own pagination, always status=INVOICE_ISSUED) ────── */
  const fetchReceivables = useCallback(async () => {
    setReceivableLoading(true);
    const params = new URLSearchParams({ page: receivablePage, limit, status: 'INVOICE_ISSUED' });
    if (receivableSearch) params.set('search', receivableSearch);
    if (receivableInvoiceNumberSortDir) params.set('invoiceNumberSortDir', receivableInvoiceNumberSortDir);
    if (filterCompany && isGlobalSuperAdmin) params.set('company', filterCompany);
    if (filterCustomer) params.set('customerId', filterCustomer.id || filterCustomer._id);
    if (enquiryTypeFilter) params.set('enquiryType', enquiryTypeFilter);
    if (invoiceMonthFilter) params.set('invoiceMonth', invoiceMonthFilter);
    const result = await apiRequest(`${API_URLS.FINANCE_SALES_INVOICES}?${params}`, {}, token);
    if (result?.ok) {
      setReceivableRows(result.data.rows || []);
      setReceivableTotal(result.data.total || 0);
    }
    setReceivableLoading(false);
  }, [token, receivablePage, limit, receivableSearch, receivableInvoiceNumberSortDir, filterCompany, isGlobalSuperAdmin, filterCustomer, enquiryTypeFilter, invoiceMonthFilter]);

  /* ── fetch notifications ────────────────────────────────── */
  const fetchNotifications = useCallback(async () => {
    const params = new URLSearchParams({ type: 'REPORT_UPLOADED', isRead: 'false', _t: Date.now() });
    if (notifCompanyFilter && isGlobalSuperAdmin) params.set('company', notifCompanyFilter);
    if (notifCustomerFilter) params.set('customerId', notifCustomerFilter.id || notifCustomerFilter._id);
    if (notifCoordinatorFilter) params.set('coordinatorId', notifCoordinatorFilter);
    const result = await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}?${params}`, {}, token);
    if (result?.ok) setNotifications(result.data.rows || []);
  }, [token, notifCompanyFilter, isGlobalSuperAdmin, notifCustomerFilter, notifCoordinatorFilter]);

  const fetchEnquiryLineItems = useCallback(async (enquiryId) => {
    if (!enquiryId) return;
    const result = await apiRequest(`${API_URLS.FINANCE_SALES_INVOICES}/from-enquiry/${enquiryId}`, {}, token);
    if (result?.ok) {
      const { lineItems: rawLineItems = [], customerName = '', customerId = '', fullyInvoiced = false } = result.data;
      if (fullyInvoiced) {
        toast.info('All inspection dates for this enquiry have already been invoiced. There is no new work to bill.');
      }
      const { invoiceCurrency, lineItems } = withDefaultInvoiceCurrency(rawLineItems, currencyRates);
      setFormData(prev => applyTotals({ ...prev, lineItems, invoiceCurrency, customerName, customerId }, currencyRates));
    } else {
      toast.error('Could not load inspector data from enquiry');
    }
  }, [token, currencyRates, toast]);

  // Re-fetch merged line items for the full accumulated set of enquiries — the single
  // source of truth for both single-job and combined invoices, so add/remove of a job
  // always recomputes fresh from the server (no client-side incremental merge).
  const refetchMerged = useCallback(async (enquiryIds) => {
    if (!enquiryIds || enquiryIds.length === 0) {
      setFormData(prev => ({ ...EMPTY_FORM, invoiceDate: prev.invoiceDate, notifIds: prev.notifIds }));
      return;
    }
    const result = await apiRequest(`${API_URLS.FINANCE_SALES_INVOICES}/from-enquiries?enquiryIds=${enquiryIds.join(',')}`, {}, token);
    if (result?.ok) {
      const { lineItems: rawLineItems = [], customerName = '', customerId = '', jobs = [], fullyInvoiced = false } = result.data;
      if (fullyInvoiced) {
        toast.info('All inspection dates for these jobs have already been invoiced. There is no new work to bill.');
      }
      // Derive enquiryIds/jobNumbers from the response's own [enquiryId, jobNumber] pairing
      // (Mongo $in does not guarantee result order matches the requested id order).
      const enquiryIdsOut = jobs.map(j => String(j.enquiryId));
      const jobNumbersOut = jobs.map(j => j.jobNumber);
      const { invoiceCurrency, lineItems } = withDefaultInvoiceCurrency(rawLineItems, currencyRates);
      setFormData(prev => applyTotals({
        ...prev, lineItems, invoiceCurrency, customerName, customerId,
        enquiryIds: enquiryIdsOut, jobNumbers: jobNumbersOut,
        enquiryId: enquiryIdsOut[0] || '', jobNumber: jobNumbersOut[0] || '',
      }, currencyRates));
    } else {
      toast.error(result?.data?.message || 'Could not load line items for these jobs');
    }
  }, [token, currencyRates, toast]);

  // Editing a saved Proforma invoice — job add/remove must keep the line items already
  // on it (with any edited rates/overtime/dates) rather than rebuilding them from scratch
  // via refetchMerged, and must not count this invoice's own dates as already invoiced.
  const isEditingProforma = modal === 'edit' && selected?.status === 'PROFORMA';

  const handleAddJob = useCallback(async (jobNumberRaw) => {
    const jobNumber = jobNumberRaw?.trim();
    if (!jobNumber) return;
    if (formData.jobNumbers?.includes(jobNumber)) {
      toast.info('This job is already added.');
      return;
    }
    const qs = new URLSearchParams({ allDates: '1' });
    if (isEditingProforma) qs.set('excludeInvoiceId', selected._id);
    const result = await apiRequest(`${API_URLS.FINANCE_SALES_INVOICES}/from-job/${encodeURIComponent(jobNumber)}?${qs}`, {}, token);
    if (!result?.ok) {
      toast.error(result?.data?.message || 'No enquiry found for this job number');
      return;
    }
    const { customerId, lineItems: newItems = [] } = result.data;
    if (formData.enquiryIds?.length > 0 && formData.customerId && customerId && String(customerId) !== String(formData.customerId)) {
      toast.error('This job belongs to a different customer — cannot combine with the jobs already added.');
      return;
    }
    if (newItems.length === 0) {
      toast.info('All inspection dates for this job have already been invoiced. There is no new work to bill.');
      return;
    }
    // Dates are picked first (AddJobDatesModal) — the job only joins the invoice on confirm.
    setAddJobPicker({ ...result.data, jobNumber: result.data.jobNumber || jobNumber });
  }, [token, isEditingProforma, selected, formData.enquiryIds, formData.customerId, formData.jobNumbers, toast]);

  // Confirm from AddJobDatesModal — appends the job's line items, each restricted to the
  // dates picked for it, keeping every line item already on the invoice as-is.
  const confirmAddJob = (pickedItems) => {
    const { enquiryId, jobNumber, customerId, customerName } = addJobPicker;
    setFormData(prev => {
      const isFirst = !(prev.lineItems || []).length;
      const invoiceCurrency = isFirst ? getInvoiceCurrency({ lineItems: pickedItems }) : (prev.invoiceCurrency || getInvoiceCurrency(prev));
      const added = pickedItems.map(li => ({ ...li, paymentRate: defaultPaymentRate(li, invoiceCurrency, currencyRates) }));
      const enquiryIds = [...(prev.enquiryIds || []), String(enquiryId)];
      const jobNumbers = [...(prev.jobNumbers || []), jobNumber];
      return applyTotals({
        ...prev,
        invoiceCurrency,
        lineItems: [...(prev.lineItems || []), ...added],
        enquiryIds, jobNumbers,
        enquiryId: enquiryIds[0], jobNumber: jobNumbers[0],
        customerId: prev.customerId || customerId, customerName: prev.customerName || customerName,
      }, currencyRates);
    });
    setAddJobPicker(null);
  };

  // Removes one job and only its own line items — the rest of the invoice (picked dates,
  // edited rates, overtime) is kept rather than rebuilt from the server.
  const handleRemoveJob = useCallback(async (jobNumber) => {
    const idx = (formData.jobNumbers || []).indexOf(jobNumber);
    if (idx === -1) return;
    if (isEditingProforma && formData.jobNumbers.length <= 1) {
      toast.info('An invoice needs at least one job — add another job before removing this one.');
      return;
    }
    toast.deleteConfirm(`Remove Job No. ${jobNumber} and all its line items from this invoice?`, () => {
      setFormData(prev => {
        const enquiryId = String(prev.enquiryIds?.[idx] || '');
        const lineItems = (prev.lineItems || []).filter(li =>
          !(li.enquiryId ? String(li.enquiryId) === enquiryId : li.jobNumber === jobNumber));
        const enquiryIds = (prev.enquiryIds || []).filter((_, i) => i !== idx);
        const jobNumbers = (prev.jobNumbers || []).filter((_, i) => i !== idx);
        const cleared = enquiryIds.length === 0 ? { customerId: '', customerName: '' } : {};
        return applyTotals({
          ...prev, ...cleared, lineItems, enquiryIds, jobNumbers,
          enquiryId: enquiryIds[0] || '', jobNumber: jobNumbers[0] || '',
        }, currencyRates);
      });
    });
  }, [isEditingProforma, formData.jobNumbers, currencyRates, toast]);

  // When a saved Proforma invoice is opened for editing, its stored line items don't
  // carry the pool of selectable dates (availableDates isn't persisted) — fetch it fresh,
  // excluding this invoice itself, and attach it to each matching line item.
  const loadAvailableDatesForEdit = useCallback(async (row, enquiryIds) => {
    if (!enquiryIds?.length) return;
    const result = await apiRequest(
      `${API_URLS.FINANCE_SALES_INVOICES}/from-enquiries?enquiryIds=${enquiryIds.join(',')}&excludeInvoiceId=${row._id}`, {}, token);
    if (!result?.ok) return;
    const keyOf = (li, withName) => `${li.enquiryId || row.enquiryId || ''}|${li.inspectorId || ''}${withName ? `|${li.inspectorName || ''}` : ''}`;
    const byKey = new Map();
    (result.data.lineItems || []).forEach(li => {
      byKey.set(keyOf(li, true), li);
      if (!byKey.has(keyOf(li, false))) byKey.set(keyOf(li, false), li);
    });
    setFormData(prev => ({
      ...prev,
      lineItems: (prev.lineItems || []).map(li => {
        const fresh = byKey.get(keyOf(li, true)) || byKey.get(keyOf(li, false));
        if (!fresh) return li;
        return {
          ...li,
          availableDates: fresh.availableDates || [],
          ...(fresh.reportedDates ? { reportedDates: fresh.reportedDates } : {}),
          ...(fresh.assignedDates ? { assignedDates: fresh.assignedDates } : {}),
          ...(fresh.workingDaysByMonth ? { workingDaysByMonth: fresh.workingDaysByMonth } : {}),
        };
      }),
    }));
  }, [token]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchReceivables(); }, [fetchReceivables]);
  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);
  useEffect(() => { fetchCurrencyRateHistory(token).then(setRateHistory); }, [token]);

  // Recomputes currencyRates as-of the form's own Invoice Date whenever that date (or
  // the rate history) changes. Smart default only: a line item still on its old default
  // rate resyncs to the new one; a manually-typed rate is left alone. Invoicing Rate
  // (INR) needs no resync — it's never baked into a line item, so it already reads
  // straight from currencyRates.
  useEffect(() => {
    const dateStr = formData.invoiceDate || new Date().toISOString().split('T')[0];
    const newRates = ratesAsOf(rateHistory, dateStr);
    const oldRates = prevCurrencyRatesRef.current;
    setFormData(prev => {
      if (!prev.lineItems || prev.lineItems.length === 0) return prev;
      const invoiceCurrency = prev.invoiceCurrency || getInvoiceCurrency(prev);
      let changed = false;
      const lineItems = prev.lineItems.map(li => {
        const oldAuto = defaultPaymentRate(li, invoiceCurrency, oldRates);
        const stored = parseFloat(li.paymentRate);
        const isFollowingDefault = isNaN(stored) || Math.abs(stored - oldAuto) < 0.0001;
        if (!isFollowingDefault) return li; // user's own override — never touch it
        const newAuto = defaultPaymentRate(li, invoiceCurrency, newRates);
        if (Math.abs(newAuto - oldAuto) < 0.0001) return li;
        changed = true;
        return { ...li, paymentRate: newAuto };
      });
      if (!changed) return prev;
      return applyTotals({ ...prev, lineItems }, newRates);
    });
    prevCurrencyRatesRef.current = newRates;
    setCurrencyRates(newRates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateHistory, formData.invoiceDate]);

  /* ── form helpers ───────────────────────────────────────── */
  const handleFormChange = (field, value) => {
    setFormData(prev => applyTotals({ ...prev, [field]: value }, currencyRates));
  };

  // Changing the invoice's own billing currency resets every line item's invoicing
  // rate to the matching auto default (offered currency → new invoice currency) —
  // any rate a user had manually overridden gets recomputed too, since "override" was
  // relative to the old currency and would otherwise silently mean something different.
  const handleInvoiceCurrencyChange = (invoiceCurrency) => {
    setFormData(prev => {
      const lineItems = (prev.lineItems || []).map(li => ({ ...li, paymentRate: defaultPaymentRate(li, invoiceCurrency, currencyRates) }));
      return applyTotals({ ...prev, invoiceCurrency, lineItems }, currencyRates);
    });
  };

  const handleAddOtherCost = () => {
    setFormData(prev => {
      const currency = prev.invoiceCurrency || getInvoiceCurrency(prev);
      const next = { ...prev, otherCosts: [...(prev.otherCosts || []), { description: '', currency, amount: '' }] };
      return applyTotals(next, currencyRates);
    });
  };

  const handleOtherCostChange = (index, field, value) => {
    setFormData(prev => {
      const otherCosts = (prev.otherCosts || []).map((oc, i) => i === index ? { ...oc, [field]: value } : oc);
      return applyTotals({ ...prev, otherCosts }, currencyRates);
    });
  };

  const handleRemoveOtherCost = (index) => {
    setFormData(prev => {
      const otherCosts = (prev.otherCosts || []).filter((_, i) => i !== index);
      return applyTotals({ ...prev, otherCosts }, currencyRates);
    });
  };

  const handleAddTax = () => {
    setFormData(prev => applyTotals({ ...prev, taxes: [...(prev.taxes || []), { type: '', percentage: '' }] }, currencyRates));
  };

  const handleTaxChange = (index, field, value) => {
    setFormData(prev => {
      const taxes = (prev.taxes || []).map((t, i) => i === index ? { ...t, [field]: value } : t);
      return applyTotals({ ...prev, taxes }, currencyRates);
    });
  };

  const handleRemoveTax = (index) => {
    setFormData(prev => {
      const taxes = (prev.taxes || []).filter((_, i) => i !== index);
      return applyTotals({ ...prev, taxes }, currencyRates);
    });
  };

  /* ── save invoice ───────────────────────────────────────── */
  const handleSave = async () => {
    if (!formData.invoiceDate) return toast.error('Invoice date is required');
    setSaving(true);
    const body = { ...formData };
    const isEdit = modal === 'edit' && selected;
    const url = isEdit ? `${API_URLS.FINANCE_SALES_INVOICES}/${selected._id}` : API_URLS.FINANCE_SALES_INVOICES;
    const method = isEdit ? 'PUT' : 'POST';
    const result = await apiRequest(url, { method, body }, token);
    if (result?.ok) {
      const savedInvoice = result.data;

      // Sync updated cost/offered values back to each source enquiry (groups by each
      // line item's own tagged enquiryId — single-job invoices are the 1-group case)
      if (formData.lineItems?.length > 0) {
        await apiRequest(
          `${API_URLS.FINANCE_SALES_INVOICES}/sync-enquiries`,
          { method: 'PATCH', body: { lineItems: formData.lineItems, fallbackEnquiryId: formData.enquiryId } },
          token
        );
      }

      // Mark every source notification as actioned now that the invoice is actually saved
      if (formData.notifIds?.length > 0) {
        await Promise.all(formData.notifIds.map(notifId =>
          apiRequest(
            `${API_URLS.FINANCE_NOTIFICATIONS}/${notifId}/actioned`,
            { method: 'PUT', body: { salesInvoiceId: savedInvoice._id } },
            token
          )
        ));
      }

      toast.success(isEdit ? 'Invoice updated' : 'Invoice created');
      // Immediately reflect the saved invoice in the table so re-opening Edit
      // before fetchInvoices completes doesn't show stale field values (e.g.
      // includeOtherCostsInTax reverted to false).
      if (isEdit && savedInvoice?._id) {
        setRows(prev => prev.map(r => String(r._id) === String(savedInvoice._id) ? { ...r, ...savedInvoice } : r));
      }
      setModal(null);
      fetchInvoices();
      fetchNotifications();
    } else {
      toast.error(result?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  /* ── customer info popup ────────────────────────────────── */
  const openCustomerInfoModal = async (customerId) => {
    if (!customerId) { toast.info('No customer linked to this invoice yet.'); return; }
    setCustomerInfoModal({ loading: true, data: null, error: null });
    const result = await apiRequest(`${API_URLS.CUSTOMERS}/${customerId}`, {}, token);
    if (result?.ok) {
      setCustomerInfoModal({ loading: false, data: result.data, error: null });
    } else {
      setCustomerInfoModal({ loading: false, data: null, error: result?.data?.message || 'Could not load customer details' });
    }
  };

  /* ── issue invoice (Proforma → Issued) ─────────────────────── */
  const handleConfirm = async (id) => {
    toast.confirm('Issue this invoice? Amounts will be locked.', async () => {
      const result = await apiRequest(`${API_URLS.FINANCE_SALES_INVOICES}/${id}/confirm`, { method: 'POST' }, token);
      if (result?.ok) { toast.success('Invoice issued'); fetchInvoices(); }
      else toast.error(result?.data?.message || 'Issue failed');
    });
  };

  /* ── receivable status ──────────────────────────────────── */
  const handleReceivableStatusChange = async (row, newStatus) => {
    const result = await apiRequest(
      `${API_URLS.FINANCE_SALES_INVOICES}/${row._id}/receivable-status`,
      { method: 'PATCH', body: { receivableStatus: newStatus } },
      token
    );
    if (result?.ok) {
      setReceivableRows(prev => prev.map(r => r._id === row._id ? { ...r, receivableStatus: newStatus } : r));
    } else {
      toast.error(result?.data?.message || 'Could not update status');
    }
  };

  /* ── sort by invoice number ────────────────────────────── */
  const toggleInvoiceNumberSort = () => {
    setInvoiceNumberSortDir(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? '' : 'asc');
    setPage(1);
  };

  const toggleReceivableInvoiceNumberSort = () => {
    setReceivableInvoiceNumberSortDir(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? '' : 'asc');
    setReceivablePage(1);
  };

  /* ── receive payment (bulk) ─────────────────────────────── */
  const toggleReceivableSelected = (rowId) => {
    setSelectedReceivableIds(prev => prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]);
  };

  const openReceivePaymentModal = (mode = 'full') => {
    const selectedRows = receivableRows.filter(r => selectedReceivableIds.includes(r._id));
    if (mode === 'partial' && selectedRows.length !== 1) return toast.info('Select exactly one invoice for a partial receipt');
    const currencies = [...new Set(selectedRows.map(r => (r.invoiceCurrency || getInvoiceCurrency(r)).toUpperCase()))];
    const currency = currencies.length === 1 ? currencies[0] : 'INR';
    const totalRemaining = selectedRows.reduce((s, r) => s + receivableBalance(r), 0);
    const today = new Date().toISOString().split('T')[0];
    // Smart default: the Currency Rate Master's rate as of today's Payment Date, falling
    // back to the "now" rate in currencyRates when history has nothing yet.
    const historyRate = currencies.length === 1 ? rateFromHistoryAsOf(rateHistory, currency, today) : null;
    setReceiveMode(mode);
    setReceivePaymentForm({
      date: today,
      // Full mode derives the cash amount (balance − TDS) in the modal itself.
      amount: mode === 'partial' ? '' : (currencies.length === 1 ? totalRemaining.toFixed(2) : ''),
      tdsAmount: '',
      rate: historyRate ?? (currencyRates[currency] ?? 1),
      bankCharges: '',
    });
    setReceiveRateTouched(false);
    setShowReceivePaymentModal(true);
  };

  const submitReceivePayment = async () => {
    if (selectedReceivableIds.length === 0) return;
    if (!receivePaymentForm.date) return toast.info('Payment date is required');
    const amount = parseFloat(receivePaymentForm.amount) || 0;
    const rate = parseFloat(receivePaymentForm.rate) || 0;
    const bankCharges = parseFloat(receivePaymentForm.bankCharges) || 0;
    const tdsAmount = parseFloat(receivePaymentForm.tdsAmount) || 0;
    if (amount < 0 || tdsAmount < 0) return toast.info('Amount and TDS cannot be negative');
    if (bankCharges < 0) return toast.info('Bank charges cannot be negative');

    let request;
    if (receiveMode === 'partial') {
      const row = receivableRows.find(r => r._id === selectedReceivableIds[0]);
      const balance = row ? receivableBalance(row) : 0;
      if (!(amount + tdsAmount > 0)) return toast.info('Enter an amount or TDS greater than 0');
      if (amount + tdsAmount > balance + 0.005) return toast.info(`Amount + TDS cannot exceed the balance (${fmt(balance)})`);
      if (bankCharges > amount + 0.005) return toast.info('Bank charges cannot exceed the payment received amount');
      request = [`${API_URLS.FINANCE_SALES_INVOICES}/${selectedReceivableIds[0]}/receive-partial`, { date: receivePaymentForm.date, amount, tdsAmount, rate, bankCharges }];
    } else {
      const totalBalance = receivableRows.filter(r => selectedReceivableIds.includes(r._id)).reduce((s, r) => s + receivableBalance(r), 0);
      if (tdsAmount > totalBalance + 0.005) return toast.info(`TDS cannot exceed the balance (${fmt(totalBalance)})`);
      // Server settles each invoice's own remaining balance and splits TDS/bankCharges across them.
      request = [`${API_URLS.FINANCE_SALES_INVOICES}/receive-payment`, { invoiceIds: selectedReceivableIds, date: receivePaymentForm.date, rate, bankCharges, tdsAmount }];
    }

    setReceivingPayment(true);
    const result = await apiRequest(request[0], { method: 'PATCH', body: request[1] }, token);
    if (result?.ok) {
      const updated = Array.isArray(result.data.updated) ? result.data.updated : [result.data.updated];
      const byId = new Map(updated.map(u => [u._id, u]));
      setReceivableRows(prev => prev.map(r => byId.has(r._id) ? { ...r, ...byId.get(r._id) } : r));
      setSelectedReceivableIds([]);
      setShowReceivePaymentModal(false);
    } else {
      toast.error(result?.data?.message || 'Could not record payment');
    }
    setReceivingPayment(false);
  };

  /* ── delete ─────────────────────────────────────────────── */
  const handleDelete = async (id) => {
    toast.deleteConfirm('Delete this proforma invoice?', async () => {
      const result = await apiRequest(`${API_URLS.FINANCE_SALES_INVOICES}/${id}`, { method: 'DELETE' }, token);
      if (result?.ok) { toast.success('Invoice deleted'); fetchInvoices(); }
      else toast.error(result?.data?.message || 'Delete failed');
    });
  };

  /* ── export ─────────────────────────────────────────────── */
  const handleExport = async (id, invoiceNumber) => {
    setDownloadingId(id);
    try {
      const res = await fetch(`${API_URLS.FINANCE_SALES_INVOICES}/${id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return toast.error('Could not download invoice.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoiceNumber || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  /* ── preview (same generated PDF as export, opened inline instead of downloaded) ── */
  const handlePreviewInvoice = async (id) => {
    setPreviewingId(id);
    try {
      const res = await fetch(`${API_URLS.FINANCE_SALES_INVOICES}/${id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return toast.error('Could not generate invoice preview.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Opened as a blob: URL (not the raw API URL), so the browser's own PDF viewer
      // renders it inline regardless of the response's Content-Disposition header.
      window.open(url, '_blank');
    } finally {
      setPreviewingId(null);
    }
  };

  /* ── create from notification ───────────────────────────── */
  const handleCreateFromNotif = async (notif) => {
    setFormData({
      ...EMPTY_FORM,
      jobNumber: notif.jobNumber || '',
      invoiceDate: notif.reportDate ? new Date(notif.reportDate).toISOString().split('T')[0] : EMPTY_FORM.invoiceDate,
      enquiryId: notif.enquiryId || '',
      enquiryIds: notif.enquiryId ? [notif.enquiryId] : [],
      jobNumbers: notif.jobNumber ? [notif.jobNumber] : [],
      notifIds: notif._id ? [notif._id] : [],
    });
    setModal('add');
    if (notif.enquiryId) fetchEnquiryLineItems(notif.enquiryId);
  };

  /* ── create one combined invoice from several selected notifications (same customer) ── */
  const handleCreateCombinedInvoice = async () => {
    const selectedNotifs = notifications.filter(n => selectedNotifIds.includes(n._id));
    const enquiryIds = [...new Set(selectedNotifs.map(n => n.enquiryId).filter(Boolean))];
    if (enquiryIds.length < 2) return;
    setFormData({ ...EMPTY_FORM, notifIds: [...selectedNotifIds] });
    setModal('add');
    await refetchMerged(enquiryIds);
  };

  const toggleNotifSelection = (notif) => {
    setSelectedNotifIds(prev => prev.includes(notif._id) ? prev.filter(id => id !== notif._id) : [...prev, notif._id]);
  };

  const lockedNotifCustomerId = selectedNotifIds.length > 0
    ? notifications.find(n => n._id === selectedNotifIds[0])?.customerId
    : null;

  /* ── dismiss a notification without creating an invoice ──── */
  const handleDismissNotif = (notif) => {
    toast.deleteConfirm(`Remove this request for Job No. ${notif.jobNumber || '—'}? No invoice will be created.`, async () => {
      const result = await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}/${notif._id}/read`, { method: 'PUT' }, token);
      if (result?.ok) {
        setNotifications(prev => prev.filter(n => n._id !== notif._id));
        setSelectedNotifIds(prev => prev.filter(id => id !== notif._id));
      } else {
        toast.error(result?.data?.message || 'Could not remove notification');
      }
    });
  };

  /* ── open edit ──────────────────────────────────────────── */
  const openEdit = (row) => {
    setSelected(row);
    setFormData({
      invoiceNumber: row.invoiceNumber || '',
      invoiceDate: row.invoiceDate ? new Date(row.invoiceDate).toISOString().split('T')[0] : '',
      dueDate: row.dueDate ? new Date(row.dueDate).toISOString().split('T')[0] : '',
      jobNumber: row.jobNumber || '',
      customerName: row.customerName || '',
      customerId: row.customerId || '',
      poNumber: row.poNumber || '',
      remarks: row.remarks || '',
      inspectorName: row.inspectorName || '',
      description: row.description || '',
      amount: row.amount ?? '',
      otherCosts: Array.isArray(row.otherCosts) && row.otherCosts.length
        ? row.otherCosts
        : (row.otherCharges ? [{ description: 'Other Charges', currency: row.invoiceCurrency || getInvoiceCurrency(row), amount: row.otherCharges }] : []),
      includeOtherCostsInTax: row.includeOtherCostsInTax || false,
      taxes: row.taxes || [],
      total: row.total ?? '',
      invoiceCurrency: row.invoiceCurrency || getInvoiceCurrency(row),
      enquiryId: row.enquiryId || '',
      lineItems: row.lineItems || [],
      enquiryIds: row.enquiryIds?.length ? row.enquiryIds : (row.enquiryId ? [row.enquiryId] : []),
      jobNumbers: row.jobNumbers?.length ? row.jobNumbers : (row.jobNumber ? [row.jobNumber] : []),
      notifIds: [],
    });
    setModal('edit');
    if (row.status === 'PROFORMA') {
      loadAvailableDatesForEdit(row, (row.enquiryIds?.length ? row.enquiryIds : (row.enquiryId ? [row.enquiryId] : [])).map(String));
    }
  };

  const closeModal = () => { setModal(null); setSelected(null); setFormData(EMPTY_FORM); };

  const statusLabel = (s) => s === 'INVOICE_ISSUED' ? 'Invoice Issued' : 'Proforma Invoice';

  const statusBadge = (s) => {
    if (s === 'INVOICE_ISSUED') return <span className="fin-badge fin-badge-confirmed">{statusLabel(s)}</span>;
    return <span className="fin-badge fin-badge-draft">{statusLabel(s)}</span>;
  };

  const fmt = (n) => n != null ? (+n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  const totalPages = Math.ceil(total / limit);
  const receivableTotalPages = Math.ceil(receivableTotal / limit);

  /* ── render ─────────────────────────────────────────────── */
  return (
    <div className="fin-page">
      <div className="fin-card">
        <div className="fin-header">
          <div>
            <h2 className="fin-title">Customer Invoice</h2>
            <p className="fin-subtitle">{total} invoices total</p>
          </div>
          <div className="fin-controls">
            <button className="fin-btn fin-btn-outline" style={{ position: 'relative' }} title="Invoice notifications" onClick={() => setShowNotifModal(true)}>
              <FiBell size={14} />
              {notifications.length > 0 && (
                <span className="fin-notif-count" style={{ position: 'absolute', top: -6, right: -6, width: 16, height: 16, fontSize: 10 }}>
                  {notifications.length}
                </span>
              )}
            </button>
            <FilterPanel
              values={{
                ...(isGlobalSuperAdmin ? { company: filterCompany } : {}),
                customer: filterCustomer?.id || filterCustomer?._id || '',
                enquiryType: enquiryTypeFilter,
                month: invoiceMonthFilter,
                ...(activeTab === 'invoices' ? { status: statusFilter } : {}),
              }}
              emptyValues={{
                ...(isGlobalSuperAdmin ? { company: '' } : {}),
                customer: '', enquiryType: '', month: '',
                ...(activeTab === 'invoices' ? { status: '' } : {}),
              }}
              onChange={(v) => {
                if (isGlobalSuperAdmin) setFilterCompany(v.company);
                setFilterCustomer(customerOptions.find(c => (c.id || c._id) === v.customer) || null);
                setEnquiryTypeFilter(v.enquiryType);
                setInvoiceMonthFilter(v.month);
                if (activeTab === 'invoices') setStatusFilter(v.status);
                setPage(1);
                setReceivablePage(1);
              }}
              width={300}
            >
              {(filters, set) => (
                <>
                  {isGlobalSuperAdmin && (
                    <FilterField label="Company">
                      <Select
                        options={[
                          { value: '', label: 'All Companies' },
                          ...companies.map(c => ({ value: c.id || c._id, label: c.companyName })),
                        ]}
                        value={{ value: filters.company, label: companies.find(c => (c.id || c._id) === filters.company)?.companyName || 'All Companies' }}
                        onChange={opt => set({ company: opt?.value || '' })}
                        styles={filterSelectStyles}
                        isClearable={false}
                        placeholder="Search company…"
                      />
                    </FilterField>
                  )}
                  <FilterField label="Customer">
                    <Select
                      options={[
                        { value: '', label: 'All Customers' },
                        ...customerOptions.map(c => ({ value: c.id || c._id, label: c.customerName })),
                      ]}
                      value={{ value: filters.customer, label: customerOptions.find(c => (c.id || c._id) === filters.customer)?.customerName || 'All Customers' }}
                      onChange={opt => set({ customer: opt?.value || '' })}
                      styles={filterSelectStyles}
                      isClearable={false}
                      placeholder="Search customer…"
                    />
                  </FilterField>
                  <FilterField label="Enquiry Type">
                    <Select
                      options={[
                        { value: '', label: 'All Enquiry Types' },
                        { value: 'MANDAY', label: 'Manday' },
                        { value: 'MANMONTH', label: 'Manmonth' },
                      ]}
                      value={{ value: filters.enquiryType, label: { MANDAY: 'Manday', MANMONTH: 'Manmonth' }[filters.enquiryType] || 'All Enquiry Types' }}
                      onChange={opt => set({ enquiryType: opt?.value || '' })}
                      styles={filterSelectStyles}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </FilterField>
                  {activeTab === 'invoices' && (
                    <FilterField label="Status">
                      <Select
                        options={[
                          { value: '', label: 'All Status' },
                          { value: 'PROFORMA', label: 'Proforma Invoice' },
                          { value: 'INVOICE_ISSUED', label: 'Invoice Issued' },
                        ]}
                        value={{ value: filters.status, label: { PROFORMA: 'Proforma Invoice', INVOICE_ISSUED: 'Invoice Issued' }[filters.status] || 'All Status' }}
                        onChange={opt => set({ status: opt?.value || '' })}
                        styles={filterSelectStyles}
                        isClearable={false}
                        isSearchable={false}
                      />
                    </FilterField>
                  )}
                  <FilterField label="Invoice Month">
                    <div className="fin-month-wrap" style={{ width: '100%' }}>
                      <input
                        type="month"
                        className={`fin-month-input${filters.month ? ' has-value' : ''}`}
                        value={filters.month}
                        onChange={e => set({ month: e.target.value })}
                      />
                      {!filters.month && <span className="fin-month-placeholder" style={{ color: '#999' }}>All Months</span>}
                    </div>
                  </FilterField>
                </>
              )}
            </FilterPanel>
            <button className="fin-btn fin-btn-outline" onClick={() => activeTab === 'receivables' ? fetchReceivables() : fetchInvoices()}><FiRefreshCw size={14} /></button>
            <div style={{ position: 'relative' }}>
              <button className="fin-btn fin-btn-outline" onClick={() => setShowRatesPopup(v => !v)}>
                <FiDollarSign size={14} /> Rates
              </button>
              {showRatesPopup && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setShowRatesPopup(false)} />
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 10,
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: 12, minWidth: 200,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Today's Rates</div>
                    {Object.keys(currencyRates).filter(c => c !== 'INR').sort().map(code => (
                      <div key={code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                        <span>{code}</span>
                        <span style={{ fontWeight: 600 }}>₹{Number(currencyRates[code]).toFixed(2)}</span>
                      </div>
                    ))}
                    <Link to="/finance/currency-rates" style={{ display: 'block', fontSize: 11, color: '#437cf0', marginTop: 8, textAlign: 'right' }}>
                      Manage rates →
                    </Link>
                  </div>
                </>
              )}
            </div>
            <button className="fin-btn fin-btn-primary" onClick={() => { setFormData(EMPTY_FORM); setModal('add'); }}>
              <FiPlus size={14} /> New Invoice
            </button>
            {activeTab === 'receivables' && selectedReceivableIds.length > 0 && (
              <>
                <button className="fin-btn fin-btn-success" onClick={() => openReceivePaymentModal('full')}>
                  <FiCheck size={14} /> Receive Payment ({selectedReceivableIds.length})
                </button>
                <button
                  className="fin-btn fin-btn-warning"
                  disabled={selectedReceivableIds.length !== 1}
                  title={selectedReceivableIds.length !== 1 ? 'Select exactly one invoice for a partial receipt' : 'Record part of this invoice\'s balance'}
                  onClick={() => openReceivePaymentModal('partial')}
                >
                  Partial Receive
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="fin-tabs">
          {['invoices', 'receivables'].map(t => (
            <button key={t} className={`fin-tab ${activeTab === t ? 'active' : ''}`} onClick={() => { setActiveTab(t); setSelectedReceivableIds([]); }}>
              {t === 'invoices' ? 'Customer Invoice' : 'Receivables'}
            </button>
          ))}
        </div>

        {activeTab === 'invoices' && (
          <>
            <div className="fin-filter-bar">
              <div className="fin-search-wrap">
                <FiSearch size={14} />
                <input className="fin-search-input" placeholder="Search invoice, job, customer…" value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }} />
              </div>
            </div>

            <div className="fin-table-wrap" style={{ border: 0 }}>
              <table className="vendor-table">
                <thead>
                  <tr>
                    <th>Job Number</th>
                    <th onClick={toggleInvoiceNumberSort} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                      Invoice Number{' '}
                      {invoiceNumberSortDir === 'asc' ? <FiChevronUp size={13} /> : invoiceNumberSortDir === 'desc' ? <FiChevronDown size={13} /> : <span style={{ color: '#bbb' }}><FiChevronUp size={11} /></span>}
                    </th>
                    <th>Date</th><th>Due Date</th><th>Customer</th>
                    <th>Amount (Excl. Tax)</th><th>Tax Amount</th><th>Total</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="fin-loading">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={10} className="fin-empty">No invoices found</td></tr>
                  ) : rows.map(row => {
                    const rowTaxAmount = (row.taxes || []).reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                    const rowAmountExclTax = (parseFloat(row.total) || 0) - rowTaxAmount;
                    return (
                    <tr key={row._id}>
                      <td><JobNumberCell row={row} /></td>
                      <td>{row.invoiceNumber || '—'}</td>
                      <td>{row.invoiceDate ? new Date(row.invoiceDate).toLocaleDateString('en-IN') : '—'}</td>
                      <td style={row.receivableStatus === 'Overdue' ? { color: '#991b1b', fontWeight: 600 } : undefined}>
                        {row.dueDate ? new Date(row.dueDate).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td>{row.customerName || '—'}</td>
                      <td>
                        <span style={{ color: '#3730a3', fontWeight: 700, marginRight: 2 }}>
                          {currencySymbol(row.invoiceCurrency)}
                        </span>
                        {fmt(rowAmountExclTax)}
                      </td>
                      <td>
                        <span style={{ color: '#3730a3', fontWeight: 700, marginRight: 2 }}>
                          {currencySymbol(row.invoiceCurrency)}
                        </span>
                        {fmt(rowTaxAmount)}
                      </td>
                      <td>
                        <span style={{ color: '#3730a3', fontWeight: 700, marginRight: 2 }}>
                          {currencySymbol(row.invoiceCurrency)}
                        </span>
                        {fmt(row.total)}
                      </td>
                      <td>{statusBadge(row.status)}</td>
                      <td>
                        <div className="fin-actions">
                          <button className="fin-icon-btn" title={previewingId === row._id ? 'Generating preview…' : 'Preview Invoice'} disabled={previewingId === row._id} onClick={() => handlePreviewInvoice(row._id)}>
                            {previewingId === row._id ? <FiRefreshCw size={14} className="fin-spin" /> : <FiEye size={14} />}
                          </button>
                          <button className="fin-icon-btn" title="Edit" onClick={() => openEdit(row)}><FiEdit size={14} /></button>
                          {row.status === 'PROFORMA' && (
                            <>
                              <button className="fin-icon-btn" title="Issue Invoice" onClick={() => handleConfirm(row._id)}><FiCheck size={14} /></button>
                              <button className="fin-icon-btn danger" title="Delete" onClick={() => handleDelete(row._id)}><FiTrash2 size={14} /></button>
                            </>
                          )}
                          <button className="fin-icon-btn" title={downloadingId === row._id ? 'Generating PDF…' : 'Download PDF'} disabled={downloadingId === row._id} onClick={() => handleExport(row._id, row.invoiceNumber)}>
                            {downloadingId === row._id ? <FiRefreshCw size={14} className="fin-spin" /> : <FiDownload size={14} />}
                          </button>
                          {row.fileUrl && (
                            <a className="fin-icon-btn" href={row.fileUrl} target="_blank" rel="noopener noreferrer" title="View Invoice (OneDrive)">
                              <FaFileInvoiceDollar size={14} />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div className="cust-pagination">
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="cust-page-info">Page {page} of {Math.max(1, totalPages)}</span>
                <button className="cust-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <button className="cust-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}

        {activeTab === 'receivables' && (
          <>
            <ReceivablesTab
              rows={receivableRows}
              fmt={fmt}
              onStatusChange={handleReceivableStatusChange}
              toast={toast}
              search={receivableSearch}
              onSearchChange={v => { setReceivableSearch(v); setReceivablePage(1); }}
              selectedIds={selectedReceivableIds}
              onToggleSelect={toggleReceivableSelected}
              loading={receivableLoading}
              invoiceNumberSortDir={receivableInvoiceNumberSortDir}
              onToggleInvoiceNumberSort={toggleReceivableInvoiceNumberSort}
            />
            {receivableTotal > 0 && (
              <div className="cust-pagination">
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setReceivablePage(1); }}
                  style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="cust-page-info">Page {receivablePage} of {Math.max(1, receivableTotalPages)}</span>
                <button className="cust-btn" disabled={receivablePage === 1} onClick={() => setReceivablePage(p => p - 1)}>Prev</button>
                <button className="cust-btn" disabled={receivablePage >= receivableTotalPages} onClick={() => setReceivablePage(p => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Invoice Form Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="fin-modal fin-modal-lg">
            <div className="fin-modal-header">
              <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center', gap: 2}}>
                <FiFileText size={18} style={{ marginRight: 6 }} />
                {modal === 'add' ? 'New Customer Invoice' : 'Edit Invoice'}
              </h3>
              <button className="fin-modal-close" onClick={closeModal}><FiX /></button>
            </div>
            <div className="fin-modal-body">
              <InvoiceForm form={formData} onChange={handleFormChange} readOnly={false} onAddJob={modal === 'add' || isEditingProforma ? handleAddJob : undefined} onRemoveJob={handleRemoveJob} currencyRates={currencyRates}
                onAddOtherCost={handleAddOtherCost} onOtherCostChange={handleOtherCostChange} onRemoveOtherCost={handleRemoveOtherCost}
                onAddTax={handleAddTax} onTaxChange={handleTaxChange} onRemoveTax={handleRemoveTax} onInvoiceCurrencyChange={handleInvoiceCurrencyChange}
                onViewCustomerInfo={openCustomerInfoModal} />
            </div>
            <div className="fin-modal-footer">
              <button className="fin-btn fin-btn-outline" onClick={closeModal}>Cancel</button>
              <button className="fin-btn fin-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {addJobPicker && (
        <AddJobDatesModal job={addJobPicker} onCancel={() => setAddJobPicker(null)} onConfirm={confirmAddJob} />
      )}

      {/* Notifications Modal */}
      {showNotifModal && (
        <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowNotifModal(false); }}>
          <div className="fin-modal" style={{ maxWidth: 900 , height : "75vh" }}>
            <div className="fin-modal-header">
              <h3 className="fin-modal-title" style={{ display: 'flex' , alignItems: 'center' }}>
                <FiBell size={18} style={{ marginRight: 6}} />
                Request to Customer Invoice - {notifications.length} job{notifications.length === 1 ? '' : 's'} ready for invoicing
              </h3>
              <button className="fin-modal-close" onClick={() => setShowNotifModal(false)}><FiX /></button>
            </div>
            <div className="fin-modal-body">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {isGlobalSuperAdmin && (
                  <select
                    className="fin-select"
                    value={notifCompanyFilter}
                    onChange={(e) => { setNotifCompanyFilter(e.target.value); setSelectedNotifIds([]); }}
                    title="Filter by company"
                  >
                    <option value="">All Companies</option>
                    {companies.map((c) => (
                      <option key={c.id || c._id} value={c.id || c._id}>{c.companyName}</option>
                    ))}
                  </select>
                )}
                <CustomerFilterDropdown
                  customers={customerOptions}
                  selected={notifCustomerFilter}
                  onSelect={(c) => { setNotifCustomerFilter(c); setSelectedNotifIds([]); }}
                />
                <select
                  className="fin-select"
                  value={notifCoordinatorFilter}
                  onChange={(e) => { setNotifCoordinatorFilter(e.target.value); setSelectedNotifIds([]); }}
                  title="Filter by coordinator"
                >
                  <option value="">All Coordinators</option>
                  {coordinatorOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.employeeName}</option>
                  ))}
                </select>
                <div className="fin-search-wrap">
                  <FiSearch size={14} />
                  <input className="fin-search-input" placeholder="Search job, customer, inspector…" value={notifSearch}
                    onChange={e => setNotifSearch(e.target.value)} />
                </div>
              </div>
              {notifications.length === 0 ? (
                <p style={{ fontSize: 13, color: '#888' }}>No pending invoice notifications.</p>
              ) : filteredNotifications.length === 0 ? (
                <p style={{ fontSize: 13, color: '#888' }}>No notifications match "{notifSearch}".</p>
              ) : (
                <>
                  {selectedNotifIds.length >= 2 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <button className="fin-btn fin-btn-primary" style={{ height: 28, fontSize: 11 }}
                        onClick={() => { handleCreateCombinedInvoice(); setShowNotifModal(false); }}>
                        <FiPlus size={12} /> Create Combined Invoice ({selectedNotifIds.length})
                      </button>
                    </div>
                  )}
                  <div className="fin-notif-list">
                    {filteredNotifications.map(n => {
                      const isChecked = selectedNotifIds.includes(n._id);
                      const isDisabled = !n.customerId || (lockedNotifCustomerId && String(n.customerId) !== String(lockedNotifCustomerId));
                      return (
                        <div key={n._id} className="fin-notif-item">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input type="checkbox" checked={isChecked} disabled={isDisabled}
                              title={!n.customerId
                                ? 'This request predates combined invoicing — resend the report to Finance to enable combining'
                                : (isDisabled ? 'Belongs to a different customer than the current selection' : 'Select to combine into one invoice')}
                              onChange={() => toggleNotifSelection(n)}
                              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }} />
                            <div className="fin-notif-item-info">
                              <span className="fin-notif-job">Job No. {n.jobNumber}</span>
                              <span className="fin-notif-meta">
                                {n.customerName ? <>Customer: {n.customerName} &bull; </> : null}
                                Inspector: {n.inspectorName || '—'} &bull; {n.reportDate ? new Date(n.reportDate).toLocaleDateString('en-IN') : '—'}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button className="fin-btn fin-btn-primary" style={{ height: 28, fontSize: 11 }}
                              onClick={() => { handleCreateFromNotif(n); setShowNotifModal(false); }}>
                              <FiPlus size={12} /> Create Invoice
                            </button>
                            <button type="button" title="Remove — no invoice will be created"
                              onClick={() => handleDismissNotif(n)}
                              style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4, display: 'flex' }}>
                              <FiX size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="fin-modal-footer">
              <button className="fin-btn fin-btn-outline" onClick={() => setShowNotifModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Receive Payment Modal */}
      {showReceivePaymentModal && (() => {
        const selectedRows = receivableRows.filter(r => selectedReceivableIds.includes(r._id));
        const jobNumbers = [...new Set(selectedRows.flatMap(r => r.jobNumbers?.length ? r.jobNumbers : (r.jobNumber ? [r.jobNumber] : [])))];
        const customerNames = [...new Set(selectedRows.map(r => r.customerName).filter(Boolean))];
        const currencies = [...new Set(selectedRows.map(r => (r.invoiceCurrency || getInvoiceCurrency(r)).toUpperCase()))];
        const currency = currencies.length === 1 ? currencies[0] : 'Mixed';
        const isPartial = receiveMode === 'partial';
        const totalInvoiceAmount = selectedRows.reduce((s, r) => s + (parseFloat(r.total) || 0), 0);
        const totalReceived = selectedRows.reduce((s, r) => s + (parseFloat(r.amountReceived) || 0), 0);
        const totalBalance = selectedRows.reduce((s, r) => s + receivableBalance(r), 0);
        const money = (v) => `${currency === 'Mixed' ? '' : currencySymbol(currency) + ' '}${fmt(v)}${currency === 'Mixed' ? ' (mixed currencies)' : ''}`;
        const tdsAmount = parseFloat(receivePaymentForm.tdsAmount) || 0;
        // Full receipt settles the whole balance, so its cash part is balance − TDS.
        const amount = isPartial ? (parseFloat(receivePaymentForm.amount) || 0) : Math.max(0, totalBalance - tdsAmount);
        const rate = parseFloat(receivePaymentForm.rate) || 0;
        const bankCharges = parseFloat(receivePaymentForm.bankCharges) || 0;
        const netInBank = amount - bankCharges; // what actually reaches the bank after the bank's cut
        const amountINR = netInBank * rate; // TDS is not cash — excluded
        const readOnlyField = (label, value) => (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
            <input type="text" className="fin-form-input" style={{ width: '100%', background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }} value={value} disabled />
          </div>
        );
        return (
          <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget && !receivingPayment) setShowReceivePaymentModal(false); }}>
            <div className="fin-modal" style={{ maxWidth: "85%" }}>
              <div className="fin-modal-header">
                <h3 className="fin-modal-title">{isPartial ? 'Partial Receive Payment' : 'Receive Payment'}</h3>
                <button className="fin-modal-close" disabled={receivingPayment} onClick={() => setShowReceivePaymentModal(false)}><FiX /></button>
              </div>
              <div className="fin-modal-body">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 20 }}>
                  {readOnlyField('Selected Invoices', selectedRows.map(r => r.invoiceNumber).join(', ') || '—')}
                  {readOnlyField('Job Number', jobNumbers.join(', ') || '—')}
                  {readOnlyField('Client Name', customerNames.join(', ') || '—')}
                  {readOnlyField('Total Invoice Amount', money(totalInvoiceAmount))}
                  {readOnlyField('Already Received', money(totalReceived))}
                  {readOnlyField('Balance', money(totalBalance))}
                  {isPartial && readOnlyField('Balance After This Payment (Amount + TDS)', money(Math.max(0, totalBalance - amount - tdsAmount)))}
                </div>

                <div className="sub-card">
                  <div className="sub-card-header expanded" style={{ cursor: 'default' }}>
                    <h2><FiDollarSign /> PAYMENT DETAILS</h2>
                  </div>
                  <div className="sub-card-content">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
                      <div>
                        <label className="fin-form-label">Payment Date *</label>
                        <input
                          type="date"
                          className="fin-form-input"
                          style={{ width: '100%' }}
                          value={receivePaymentForm.date}
                          onChange={e => {
                            const newDate = e.target.value;
                            setReceivePaymentForm(prev => {
                              // Smart default only — re-derives the rate from the Currency
                              // Rate Master as of the new date unless manually typed, or the
                              // selection is mixed-currency (no single rate to look up).
                              if (receiveRateTouched || currency === 'Mixed') return { ...prev, date: newDate };
                              const newRate = rateFromHistoryAsOf(rateHistory, currency, newDate);
                              return { ...prev, date: newDate, ...(newRate != null ? { rate: newRate } : {}) };
                            });
                          }}
                        />
                      </div>
                      <div>
                        <label className="fin-form-label">
                          Payment Received Amount ({currency === 'Mixed' ? currency : currencySymbol(currency)}){isPartial ? ' *' : ''}
                        </label>
                        {/* Full receipt always covers each invoice's whole remaining balance,
                            so the amount is fixed there (balance − TDS) — only a partial
                            receipt is typed. */}
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={isPartial ? totalBalance : undefined}
                          className="fin-form-input"
                          style={isPartial ? { width: '100%' } : { width: '100%', background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }}
                          disabled={!isPartial}
                          value={isPartial ? receivePaymentForm.amount : (currency === 'Mixed' ? '' : amount.toFixed(2))}
                          onChange={e => setReceivePaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="fin-form-label" title="Tax deducted at source by the customer — settles the invoice but isn't received in the bank">
                          TDS Amount ({currency === 'Mixed' ? currency : currencySymbol(currency)})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="fin-form-input"
                          style={{ width: '100%' }}
                          value={receivePaymentForm.tdsAmount}
                          onChange={e => setReceivePaymentForm(prev => ({ ...prev, tdsAmount: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="fin-form-label">Total Settled (Amount + TDS)</label>
                        <input type="text" className="fin-form-input" style={{ width: '100%', background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }} value={money(amount + tdsAmount)} disabled />
                      </div>
                      <div>
                        <label className="fin-form-label">Payment Received Rate</label>
                        <input
                          type="number"
                          step="0.0001"
                          className="fin-form-input"
                          style={{ width: '100%' }}
                          value={receivePaymentForm.rate}
                          onChange={e => { setReceiveRateTouched(true); setReceivePaymentForm(prev => ({ ...prev, rate: e.target.value })); }}
                        />
                      </div>
                      <div>
                        <label className="fin-form-label">Net Amount in INR (after Bank Charges)</label>
                        <input type="text" className="fin-form-input" style={{ width: '100%', background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }} value={`₹${fmt(amountINR)}`} disabled />
                      </div>
                      <div>
                        <label className="fin-form-label">Bank Charges ({currency === 'Mixed' ? currency : currencySymbol(currency)})</label>
                        <input
                          type="number"
                          step="0.01"
                          className="fin-form-input"
                          style={{ width: '100%' }}
                          value={receivePaymentForm.bankCharges}
                          onChange={e => setReceivePaymentForm(prev => ({ ...prev, bankCharges: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="fin-form-label">Net Amount in Bank (Amount − Bank Charges)</label>
                        <input
                          type="text"
                          className="fin-form-input"
                          style={{ width: '100%', background: '#f3f4f6', color: netInBank < 0 ? '#dc2626' : '#6b7280', fontWeight: netInBank < 0 ? 700 : 400, cursor: 'not-allowed' }}
                          value={money(netInBank)}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="fin-modal-footer">
                <button className="fin-btn fin-btn-outline" disabled={receivingPayment} onClick={() => setShowReceivePaymentModal(false)}>Cancel</button>
                <button className="fin-btn fin-btn-success" disabled={receivingPayment} onClick={submitReceivePayment}>
                  {receivingPayment ? 'Saving…' : isPartial ? 'Confirm Partial Payment' : 'Confirm Payment'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}


      {/* Customer Info Modal — the info icon next to "Customer Name" on the invoice form */}
      {customerInfoModal && (() => {
        const c = customerInfoModal.data;
        const clientState = c?.state || '';
        const clientStateCode = GST_STATE_CODES[clientState.trim().toLowerCase()] || '';
        const field = (label, value) => (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
            <input type="text" className="fin-form-input" style={{ width: '100%', background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }} value={value || '—'} disabled />
          </div>
        );
        return (
          <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setCustomerInfoModal(null); }}>
            <div className="fin-modal" style={{ maxWidth: 460 }}>
              <div className="fin-modal-header">
                <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center' }}>
                  <FiInfo size={16} style={{ marginRight: 6 }} /> Customer Details
                </h3>
                <button className="fin-modal-close" onClick={() => setCustomerInfoModal(null)}><FiX /></button>
              </div>
              <div className="fin-modal-body">
                {customerInfoModal.loading ? (
                  <p style={{ fontSize: 13, color: '#888' }}>Loading…</p>
                ) : customerInfoModal.error ? (
                  <p style={{ fontSize: 13, color: '#dc2626' }}>{customerInfoModal.error}</p>
                ) : (
                  <>
                    {field('Customer Name', c.customerName)}
                    {field('GSTIN / VAT Number', c.gstVatNumber)}
                    {field('Customer Coordinator', c.coordinators?.length
                      ? c.coordinators.map(co => co.name).filter(Boolean).join(', ')
                      : null)}
                    {field('State', clientState)}
                    {field('Code', clientStateCode)}
                  </>
                )}
              </div>
              <div className="fin-modal-footer">
                <button className="fin-btn fin-btn-outline" onClick={() => setCustomerInfoModal(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ── Invoice Form ─────────────────────────────────────────── */
function InvoiceForm({ form, onChange, readOnly, onAddJob, onRemoveJob, currencyRates = DEFAULT_CURRENCY_RATES, onAddOtherCost, onOtherCostChange, onRemoveOtherCost, onAddTax, onTaxChange, onRemoveTax, onInvoiceCurrencyChange, onViewCustomerInfo }) {
  const [jobInput, setJobInput] = useState('');
  const [dueDatePreset, setDueDatePreset] = useState('');
  const [showInrRatesModal, setShowInrRatesModal] = useState(false);
  const fmt = n => n != null
    ? (+n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
  // Only paymentRate (invoicing rate), invoicingRateINR, and the overtime fields are
  // editable — everything else is locked from Manday. `updates` may set several fields
  // at once (e.g. clearing all 4 overtime fields together) — accepting an object rather
  // than a single field/value pair keeps that one atomic update, instead of firing
  // several onChange calls that would each recompute from the same stale
  // `form.lineItems` and clobber each other. Shared by LineItemsTable and the
  // Invoicing Rate/Price (INR) modal below.
  const handleLineItemChange = (index, updates) => {
    const updated = form.lineItems.map((li, i) => (i === index ? { ...li, ...updates } : li));
    onChange('lineItems', updated);
  };
  // Shift-click range anchor for each overtime date picker — one persistent ref per
  // row (keyed like the rows themselves), matching the tentativeAnchorRef pattern in
  // src/pages/Manday.jsx. Lazily created so the map only grows as rows are added.
  const otAnchorRefs = useRef({});
  const getOtAnchorRef = (key) => {
    if (!otAnchorRefs.current[key]) otAnchorRefs.current[key] = { current: null };
    return otAnchorRefs.current[key];
  };
  const inp = (field, type = 'text', placeholder = '') => (
    <input className="fin-form-input" type={type} placeholder={placeholder} value={form[field] ?? ''}
      disabled={readOnly} onChange={e => onChange(field, e.target.value)} />
  );

  const hasLineItems = Array.isArray(form.lineItems) && form.lineItems.length > 0;
  const invoiceCurrency = form.invoiceCurrency || getInvoiceCurrency(form);
  const otherCostsList = Array.isArray(form.otherCosts) && form.otherCosts.length
    ? form.otherCosts
    : (form.otherCharges ? [{ description: 'Other Charges', currency: invoiceCurrency, amount: form.otherCharges }] : []);
  const taxesList = Array.isArray(form.taxes) ? form.taxes : [];
  const jobNumbers = form.jobNumbers?.length ? form.jobNumbers : (form.jobNumber ? [form.jobNumber] : []);
  const showJobColumn = new Set((form.lineItems || []).map(li => li.jobNumber).filter(Boolean)).size > 1;

  const submitJobInput = () => {
    if (!jobInput.trim()) return;
    onAddJob?.(jobInput);
    setJobInput('');
  };

  // Overtime — tied to a specific inspector/line item, but shown in its own box
  // (styled like Other Costs) rather than inline in the Inspector Line Items table.
  // Each line item holds an ARRAY of overtime entries (li.overtime), so "Add OT" is
  // always clickable — it just pushes another entry, even onto the same inspector.
  const lineItemsForOT = form.lineItems || [];
  // Flat list of every overtime entry across all line items, for rendering as rows.
  const otRows = lineItemsForOT.flatMap((li, lineIdx) =>
    (li.overtime || []).map((entry, entryIdx) => ({ lineIdx, entryIdx, entry }))
  );

  // DatePicker `value` must stay referentially stable across renders (see prior fix) —
  // only rebuild when the actual date strings (or row count) change.
  const otDatesKey = otRows.map(r => (r.entry.dates || []).join(',')).join('|');
  const otDatesValues = useMemo(
    () => otRows.map(r => (r.entry.dates || []).map(dt => new Date(dt)).filter(dt => !isNaN(dt))),
    [otRows.length, otDatesKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleOvertimeEntryChange = (lineIdx, entryIdx, updates) => {
    const updated = lineItemsForOT.map((li, i) => {
      if (i !== lineIdx) return li;
      const overtime = (li.overtime || []).map((ot, ei) => (ei === entryIdx ? { ...ot, ...updates } : ot));
      return { ...li, overtime };
    });
    onChange('lineItems', updated);
  };

  const handleAddOvertime = () => {
    if (lineItemsForOT.length === 0) return;
    const updated = lineItemsForOT.map((li, i) =>
      (i === 0 ? { ...li, overtime: [...(li.overtime || []), { dates: [], hours: '', rate: '', totalAmount: 0 }] } : li)
    );
    onChange('lineItems', updated);
  };

  const handleRemoveOvertime = (lineIdx, entryIdx) => {
    const updated = lineItemsForOT.map((li, i) => {
      if (i !== lineIdx) return li;
      return { ...li, overtime: (li.overtime || []).filter((_, ei) => ei !== entryIdx) };
    });
    onChange('lineItems', updated);
  };

  const handleReassignOvertime = (oldLineIdx, entryIdx, newLineIdx) => {
    if (oldLineIdx === newLineIdx) return;
    const entry = lineItemsForOT[oldLineIdx]?.overtime?.[entryIdx];
    if (!entry) return;
    const updated = lineItemsForOT.map((li, i) => {
      if (i === oldLineIdx) return { ...li, overtime: (li.overtime || []).filter((_, ei) => ei !== entryIdx) };
      if (i === newLineIdx) return { ...li, overtime: [...(li.overtime || []), entry] };
      return li;
    });
    onChange('lineItems', updated);
  };

  return (
    <div className="fin-form-grid">
      <div className="fin-form-group full" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: 20, alignItems: 'start' }}>
      <div className="fin-form-group">
        <label className="fin-form-label" title="Leave blank to auto-generate the next invoice number">Invoice Number</label>
        {inp('invoiceNumber', 'text', 'Auto-generated if blank')}
      </div>
      <div className="fin-form-group">
        <label className="fin-form-label">Invoice Date *</label>
        {inp('invoiceDate', 'date')}
      </div>
      <div className="fin-form-group">
        <label className="fin-form-label" title="Once this date passes, the receivable automatically shows as Overdue">Due Date</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {inp('dueDate', 'date')}
          {!readOnly && (
            <select
              className="fin-form-input"
              style={{ flex: '0 0 74px' }}
              value={dueDatePreset}
              title="Set due date as invoice date + N days"
              disabled={!form.invoiceDate}
              onChange={e => {
                setDueDatePreset(e.target.value);
                const days = parseInt(e.target.value, 10);
                if (!days || !form.invoiceDate) return;
                const d = new Date(form.invoiceDate);
                d.setDate(d.getDate() + days);
                onChange('dueDate', d.toISOString().split('T')[0]);
              }}
            >
              <option value="">Days</option>
              <option value="30">30</option>
              <option value="45">45</option>
              <option value="60">60</option>
              <option value="75">75</option>
              <option value="90">90</option>
            </select>
          )}
        </div>
      </div>
      <div className="fin-form-group">
        <label className="fin-form-label">Job Number{jobNumbers.length > 1 ? 's' : ''}</label>
        {!readOnly && onAddJob ? (
          <>
            <div style={{ display: 'flex', gap: 6, marginBottom: jobNumbers.length ? 6 : 0 }}>
              <input className="fin-form-input" type="text" placeholder="e.g. APS-001"
                value={jobInput}
                onChange={e => setJobInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitJobInput()}
                style={{ flex: 1 }} />
              <button type="button" className="fin-btn fin-btn-outline"
                style={{ padding: '0 10px', whiteSpace: 'nowrap' }}
                onClick={submitJobInput}>
                <FiSearch size={13} /> Add
              </button>
            </div>
            {jobNumbers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {jobNumbers.map((jn, i) => (
                  <span key={jn + i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eef2ff', color: '#3730a3', borderRadius: 12, padding: '3px 8px', fontSize: 12 }}>
                    {jn}
                    {onRemoveJob && (
                      <button type="button" onClick={() => onRemoveJob(jn)}
                        style={{ background: 'none', border: 'none', color: '#3730a3', cursor: 'pointer', display: 'flex', padding: 0 }}>
                        <FiX size={11} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </>
        ) : (
          <input className="fin-form-input" type="text" disabled
            value={jobNumbers.join(', ')} placeholder="—" style={{ width: '100%' }} />
        )}
      </div>
      <div className="fin-form-group">
        <label className="fin-form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Customer Name
          {onViewCustomerInfo && (
            <button type="button" title="View customer details" onClick={() => onViewCustomerInfo(form.customerId)}
              style={{ background: 'none', border: 'none', color: '#437cf0', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <FiInfo size={13} />
            </button>
          )}
        </label>
        {inp('customerName', 'text', 'Customer')}
      </div>
      <div className="fin-form-group">
        <label className="fin-form-label">PO Number</label>
        {inp('poNumber', 'text', 'PO Number')}
      </div>
      <div className="fin-form-group">
        <label className="fin-form-label">Invoice Currency</label>
        {readOnly ? (
          <span style={{ fontWeight: 600 }}>{invoiceCurrency}</span>
        ) : (
          <select
            className="fin-form-input"
            value={invoiceCurrency}
            onChange={e => onInvoiceCurrencyChange?.(e.target.value)}
            title="The currency the customer is actually billed in — independent of what each inspector was offered in"
          >
            {Object.keys(currencyRates).map(code => <option key={code} value={code}>{code}</option>)}
          </select>
        )}
      </div>
      </div>
      {hasLineItems && (
        <div className="fin-form-group full">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1a3a6b' }}>Inspector Line Items</div>
            <button type="button" title="View / edit Invoicing Rate (INR) & Invoicing Price (INR)" onClick={() => setShowInrRatesModal(true)}
              style={{ background: 'none', border: 'none', color: '#437cf0', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <FiDollarSign size={14} />
            </button>
          </div>
          <LineItemsTable
            lineItems={form.lineItems}
            editable={!readOnly}
            showJobColumn={showJobColumn}
            invoiceCurrency={invoiceCurrency}
            currencyRates={currencyRates}
            onLineItemChange={handleLineItemChange}
          />
        </div>
      )}

      {/* Invoicing Rate/Price (INR) Modal — reference-only figures, moved out of the
          main table via the $ icon next to "Inspector Line Items" to keep it compact. */}
      {showInrRatesModal && (
        <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowInrRatesModal(false); }}>
          <div className="fin-modal" style={{ maxWidth: 480 }}>
            <div className="fin-modal-header">
              <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center' }}>
                <FiDollarSign size={16} style={{ marginRight: 6 }} /> Invoicing Rate / Price (INR)
              </h3>
              <button className="fin-modal-close" onClick={() => setShowInrRatesModal(false)}><FiX /></button>
            </div>
            <div className="fin-modal-body">
              <div className="fin-table-wrap">
                <table className="fin-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Inspector</th>
                      <th style={{ textAlign: 'right' }}>Invoicing Rate (INR)</th>
                      <th style={{ textAlign: 'right' }}>Invoicing Price (INR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lineItems.map((li, i) => {
                      const autoRate = defaultPaymentRate(li, invoiceCurrency, currencyRates);
                      const pRateParsed = parseFloat(li.paymentRate ?? autoRate);
                      const pRate = isNaN(pRateParsed) ? null : pRateParsed;
                      const pINR = pRate == null ? null : +(parseFloat(li.offered || 0) * pRate).toFixed(2);
                      const invoiceCurrencyToINR = currencyRates[invoiceCurrency] ?? 1;
                      const rateINRParsed = parseFloat(li.invoicingRateINR ?? invoiceCurrencyToINR);
                      const rateINR = isNaN(rateINRParsed) ? null : rateINRParsed;
                      const isRateINROverridden = li.invoicingRateINR != null && Math.abs(rateINR - invoiceCurrencyToINR) > 0.0001;
                      const pINRFixed = (pINR == null || rateINR == null) ? null : +(pINR * rateINR).toFixed(2);
                      return (
                        <tr key={i}>
                          <td>{li.inspectorName || '—'}</td>
                          <td style={{ textAlign: 'right' }}>
                            {readOnly ? (
                              <span style={{ fontWeight: 600 }}>{rateINR}</span>
                            ) : (
                              <input
                                type="number"
                                step="0.0001"
                                value={li.invoicingRateINR ?? invoiceCurrencyToINR}
                                onChange={e => handleLineItemChange(i, { invoicingRateINR: e.target.value })}
                                title={`Reference-only INR rate for this line item — does not affect Invoicing Rate or the invoice total. Default (invoice currency's rate): ${invoiceCurrencyToINR}`}
                                style={{
                                  width: 80, padding: '2px 4px', fontSize: 12, textAlign: 'right',
                                  border: `1px solid ${isRateINROverridden ? '#3b82f6' : '#d1d5db'}`,
                                  borderRadius: 4,
                                  background: isRateINROverridden ? '#dbeafe' : '#f0f7ff',
                                  fontWeight: isRateINROverridden ? 700 : 400,
                                }}
                              />
                            )}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#9ca3af' }}>{fmt(pINRFixed)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="fin-modal-footer">
              <button className="fin-btn fin-btn-outline" onClick={() => setShowInrRatesModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {!hasLineItems && (
        <div className="fin-form-group">
          <label className="fin-form-label">Amount</label>
          {inp('amount', 'number', '0.00')}
        </div>
      )}
      
      {hasLineItems && (
        <div className="fin-form-group full">
            {otRows.length > 0 && (
              <div className="fin-table-wrap" style={{ marginBottom: readOnly ? 0 : 8, width: '100%' }}>
                <table className="fin-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 150 }}>Inspector</th>
                      <th >Dates</th>
                      <th style={{ textAlign: 'right' , width : 80 }}>Total Hours</th>
                      <th style={{ textAlign: 'right', width : 80 }}>Rate/Hour</th>
                      <th style={{ textAlign: 'right' }}>Amount ({invoiceCurrency})</th>
                      {!readOnly && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {otRows.map(({ lineIdx, entryIdx, entry }, rowPos) => {
                      const otTotal = overtimeEntryAmount(entry);
                      return (
                        <tr key={`${lineIdx}-${entryIdx}`}>
                          <td>
                            {readOnly ? (lineItemsForOT[lineIdx]?.inspectorName || '—') : (
                              <select className="fin-form-input" value={lineIdx}
                                onChange={e => handleReassignOvertime(lineIdx, entryIdx, parseInt(e.target.value, 10))}>
                                {lineItemsForOT.map((li2, i2) => (
                                  <option key={i2} value={i2}>
                                    {li2.inspectorName || `Inspector ${i2 + 1}`}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            {readOnly ? ((entry.dates || []).map(fmtDDMMYY).join(', ') || '—') : (
                              <DatePicker
                                multiple
                                format="DD-MM-YYYY"
                                value={otDatesValues[rowPos]}
                                onChange={(dates) => handleOvertimeEntryChange(lineIdx, entryIdx, { dates: dates ? dates.map(d => d.format('YYYY-MM-DD')) : [] })}
                                mapDays={makeShiftRangeMapDays({
                                  getValues: () => entry.dates || [],
                                  setValues: (arr) => handleOvertimeEntryChange(lineIdx, entryIdx, { dates: arr }),
                                  anchorRef: getOtAnchorRef(`${lineIdx}-${entryIdx}`),
                                })}
                                placeholder="Select dates (Shift+click for range)"
                                inputClass="fin-form-input"
                                style={{ width: '100%', fontSize: 11, boxSizing: 'border-box' }}
                                containerStyle={{ width: '100%' }}
                              />
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {readOnly ? (entry.hours || 0) : (
                              <input className="fin-form-input" type="number" placeholder="0" style={{ textAlign: 'right' }}
                                value={entry.hours ?? ''}
                                onChange={e => handleOvertimeEntryChange(lineIdx, entryIdx, {
                                  hours: e.target.value,
                                  totalAmount: +overtimeEntryAmount({ ...entry, hours: e.target.value }).toFixed(2),
                                })} />
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {readOnly ? (entry.rate || 0) : (
                              <input className="fin-form-input" type="number" placeholder="0.00" style={{ textAlign: 'right' }}
                                value={entry.rate ?? ''}
                                onChange={e => handleOvertimeEntryChange(lineIdx, entryIdx, {
                                  rate: e.target.value,
                                  totalAmount: +overtimeEntryAmount({ ...entry, rate: e.target.value }).toFixed(2),
                                })} />
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {otTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          {!readOnly && (
                            <td>
                              <button type="button" title="Remove" onClick={() => handleRemoveOvertime(lineIdx, entryIdx)}
                                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4, display: 'flex' }}>
                                <FiX size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!readOnly && (
              <button type="button" className="fin-btn fin-btn-outline" style={{ width : "fit-content"}} onClick={handleAddOvertime}>
                <FiPlus size={13} /> Add OT
              </button>
            )}
            {readOnly && otRows.length === 0 && <span style={{ color: '#888', fontSize: 13 }}>—</span>}
        </div>
      )}

      <div className="fin-form-group full" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'end' }}>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, marginBottom: 8, cursor: readOnly ? 'default' : 'pointer' }}>
            <input type="checkbox" checked={!!form.includeOtherCostsInTax} disabled={readOnly}
              onChange={e => onChange('includeOtherCostsInTax', e.target.checked)} />
            Include Other Cost in Tax
          </label>
          {otherCostsList.length > 0 && (
            <div className="fin-table-wrap" style={{ marginBottom: readOnly ? 0 : 8, width: '100%' }}>
              <table className="fin-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Other Cost</th>
                    <th style={{ textAlign: 'right' }}>Amount ({invoiceCurrency})</th>
                    {!readOnly && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {otherCostsList.map((oc, i) => (
                    <tr key={i}>
                      <td>
                        {readOnly ? (oc.description || '—') : (
                          <input className="fin-form-input" type="text" placeholder="Description"
                            value={oc.description ?? ''}
                            onChange={e => onOtherCostChange?.(i, 'description', e.target.value)} />
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {readOnly ? (oc.amount != null ? (+oc.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—') : (
                          <input className="fin-form-input" type="number" placeholder="0.00" style={{ textAlign: 'right' }}
                            value={oc.amount ?? ''}
                            onChange={e => onOtherCostChange?.(i, 'amount', e.target.value)} />
                        )}
                      </td>
                      {!readOnly && (
                        <td>
                          <button type="button" title="Remove" onClick={() => onRemoveOtherCost?.(i)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4, display: 'flex' }}>
                            <FiX size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!readOnly && (
            <button type="button" className="fin-btn fin-btn-outline" onClick={onAddOtherCost}>
              <FiPlus size={13} /> Add Other Cost
            </button>
          )}
          {readOnly && otherCostsList.length === 0 && <span style={{ color: '#888', fontSize: 13 }}>—</span>}
        </div>

        <div>
        {taxesList.length > 0 && (
          <div className="fin-table-wrap" style={{ marginBottom: readOnly ? 0 : 8, width: '100%' }}>
            <table className="fin-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Tax Type</th>
                  <th style={{ textAlign: 'right' , width : 20 }}>Percentage (%)</th>
                  <th style={{ textAlign: 'right' }}>Amount ({invoiceCurrency})</th>
                  {!readOnly && <th></th>}
                </tr>
              </thead>
              <tbody>
                {taxesList.map((t, i) => {
                  const pct = parseFloat(t.percentage) || 0;
                  // t.amount is kept in sync with percentage by applyTotals — already in
                  // the invoice's own currency (taxBase is), no further conversion needed.
                  const amt = parseFloat(t.amount) || 0;
                  return (
                    <tr key={i}>
                      <td>
                        {readOnly ? (t.type || '—') : (
                          <select className="fin-form-input"
                            value={t.type ?? ''}
                            onChange={e => onTaxChange?.(i, 'type', e.target.value)}>
                            <option value="">Select</option>
                            <option value="CGST">CGST</option>
                            <option value="IGST">IGST</option>
                            <option value="SGST">SGST</option>
                            <option value="VAT">VAT</option>
                          </select>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {readOnly ? `${pct}%` : (
                          <input className="fin-form-input" type="number" placeholder="0" style={{ textAlign: 'right' }}
                            value={t.percentage ?? ''}
                            onChange={e => onTaxChange?.(i, 'percentage', e.target.value)} />
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      {!readOnly && (
                        <td>
                          <button type="button" title="Remove" onClick={() => onRemoveTax?.(i)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4, display: 'flex' }}>
                            <FiX size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!readOnly && (
          <button style={{width : "fit-content"}} type="button" className="fin-btn fin-btn-outline" onClick={onAddTax}>
            <FiPlus size={13} /> Add Tax
          </button>
        )}
        {readOnly && taxesList.length === 0 && <span style={{ color: '#888', fontSize: 13 }}>—</span>}
        </div>
      </div>

      <div className="fin-form-group full" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
        <div className="fin-form-group">
          <label className="fin-form-label">Remarks</label>
          <textarea
            className="fin-form-input"
            placeholder="Enter remarks…"
            value={form.remarks ?? ''}
            disabled={readOnly}
            rows={2}
            style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
            onChange={e => onChange('remarks', e.target.value)}
          />
        </div>
        <div className="fin-form-group">
          <label className="fin-form-label">Total ({invoiceCurrency})</label>
          <input className="fin-form-input" type="number" value={form.total ?? ''} disabled style={{ fontWeight: 600 }} />
        </div>
      </div>
    </div>
  );
}

/* ── Add Job: pick dates first ────────────────────────────── */
// Shown after a job number is entered in the invoice form's Add box. Lists every
// inspector on the job with its uninvoiced dates; only dates with an uploaded report can
// be ticked. Starts from the coordinator's Send-to-Finance selection when there is one,
// otherwise every reported date. Inspectors left with no dates are not added.
function AddJobDatesModal({ job, onCancel, onConfirm }) {
  const items = job.lineItems || [];
  const notifSel = job.notifSelectedDates || [];
  const hasDateList = li => Array.isArray(li.availableDates) && li.availableDates.length > 0;
  const reportedOf = li => (Array.isArray(li.reportedDates) ? li.reportedDates : li.availableDates || []);

  const [picked, setPicked] = useState(() => items.map(li => {
    if (!hasDateList(li)) return notifSel.length === 0 || notifSel.some(s => String(s.inspectorId) === String(li.inspectorId));
    const reported = reportedOf(li);
    if (notifSel.length === 0) return reported;
    const sel = new Set(notifSel.filter(s => String(s.inspectorId) === String(li.inspectorId)).flatMap(s => s.dates || []));
    return reported.filter(d => sel.has(d));
  }));

  const toggleDate = (i, dt) => setPicked(prev => prev.map((p, idx) => {
    if (idx !== i) return p;
    return p.includes(dt) ? p.filter(x => x !== dt) : [...p, dt].sort();
  }));
  const setItem = (i, val) => setPicked(prev => prev.map((p, idx) => (idx === i ? val : p)));

  const totalPicked = picked.reduce((s, p) => s + (Array.isArray(p) ? p.length : (p ? 1 : 0)), 0);

  const handleConfirm = () => {
    const out = items.flatMap((li, i) => {
      const p = picked[i];
      if (!hasDateList(li)) return p ? [li] : [];
      return p.length ? [{ ...li, ...lineItemDateUpdates(li, p) }] : [];
    });
    if (out.length) onConfirm(out);
  };

  return (
    <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="fin-modal" style={{ maxWidth: 640 }}>
        <div className="fin-modal-header">
          <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiCalendar size={18} style={{ marginRight: 6 }} /> Select Dates - Job No. {job.jobNumber}
          </h3>
          <button className="fin-modal-close" onClick={onCancel}><FiX /></button>
        </div>
        <div className="fin-modal-body">
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
            Select the dates to invoice for each inspector. Dates marked <span style={{ color: '#b91c1c', fontWeight: 600 }}>No report</span> have no uploaded report and can't be selected.
          </div>
          {items.map((li, i) => {
            const p = picked[i];
            if (!hasDateList(li)) {
              return (
                <div key={i} style={{ padding: '10px 0', borderTop: i ? '1px solid #e5e7eb' : 0 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                    <input type="checkbox" checked={!!p} onChange={e => setItem(i, e.target.checked)} />
                    {li.inspectorName || '—'} <span style={{ fontWeight: 400, color: '#6b7280' }}>({li.numberOfDays} days, no date list)</span>
                  </label>
                </div>
              );
            }
            const reportedSet = new Set(reportedOf(li));
            const selectable = li.availableDates.filter(d => reportedSet.has(d));
            return (
              <div key={i} style={{ padding: '10px 0', borderTop: i ? '1px solid #e5e7eb' : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {li.inspectorName || '—'} <span style={{ fontWeight: 400, color: '#6b7280' }}>· {p.length} of {li.availableDates.length} selected</span>
                  </span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="fin-btn fin-btn-outline" style={{ padding: '2px 8px', fontSize: 11 }}
                      disabled={selectable.length === 0 || p.length === selectable.length} onClick={() => setItem(i, selectable)}>All</button>
                    <button type="button" className="fin-btn fin-btn-outline" style={{ padding: '2px 8px', fontSize: 11 }}
                      disabled={p.length === 0} onClick={() => setItem(i, [])}>None</button>
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {li.availableDates.map(dt => {
                    const on = p.includes(dt);
                    const missingReport = !reportedSet.has(dt);
                    return (
                      <button key={dt} type="button" disabled={missingReport} onClick={() => toggleDate(i, dt)}
                        title={missingReport ? 'Report not uploaded for this date — cannot be invoiced' : (on ? 'Exclude from this invoice' : 'Include in this invoice')}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: on ? '#eef2ff' : (missingReport ? '#f9fafb' : '#fff'),
                          color: on ? '#3730a3' : (missingReport ? '#d1d5db' : '#9ca3af'),
                          border: `1px solid ${on ? '#a5b4fc' : '#e5e7eb'}`, borderRadius: 12, padding: '3px 8px', fontSize: 12,
                          cursor: missingReport ? 'not-allowed' : 'pointer',
                        }}>
                        {on && <FiCheck size={11} />}{fmtDDMMYY(dt)}
                        {missingReport && <span style={{ color: '#b91c1c', fontWeight: 600, fontSize: 10 }}>No report</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="fin-modal-footer">
          <button className="fin-btn fin-btn-outline" onClick={onCancel}>Cancel</button>
          <button className="fin-btn fin-btn-primary" disabled={totalPicked === 0} onClick={handleConfirm}
            title={totalPicked === 0 ? 'Select at least one date with an uploaded report' : ''}>
            Add Job ({totalPicked} date{totalPicked === 1 ? '' : 's'})
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Line Items Table ─────────────────────────────────────── */
function LineItemsTable({ lineItems, editable = false, onLineItemChange, showJobColumn = false, invoiceCurrency = 'INR', currencyRates = DEFAULT_CURRENCY_RATES }) {
  const [datesModalIndex, setDatesModalIndex] = useState(null);
  const fmt = n => n != null
    ? (+n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

  const daysOf = li => li.numberOfDays || li.inspectionDates?.length || 0;
  // Offer Price/Offered INR are per-day for a Manday enquiry but per-month for a
  // Manmonth one (li.offered is a monthly rate there) — label the column for whichever
  // this invoice's line items actually are. A combined invoice can mix enquiry types
  // (see enquiryIds), so fall back to a neutral label when they don't all agree.
  const allManmonth = lineItems.length > 0 && lineItems.every(li => li.enquiryType === 'MANMONTH');
  const allManday = lineItems.every(li => li.enquiryType !== 'MANMONTH');
  const offerUnit = allManmonth ? '/Month' : allManday ? '/Day' : '';
  const totalOfferedINR = lineItems.reduce((s, li) => s + totalOfferedINROf(li), 0);
  const grandTotal = lineItems.reduce((s, li) => {
    // `|| 0` covers a line item's rate being mid-edit (briefly '' while a field is
    // cleared to type a new value) — without it, one NaN would blank out this whole total.
    const pRate = parseFloat(li.paymentRate ?? defaultPaymentRate(li, invoiceCurrency, currencyRates)) || 0;
    return s + totalOfferOf(li) * pRate;
  }, 0);

  return (
    <>
    <div className="fin-table-wrap" style={{ marginTop: 4, overflowX: 'auto', border: 0 }}>
      <table className="vendor-table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            {showJobColumn && <th>Job Number</th>}
            <th>Inspector</th>
            <th style={{ minWidth : "100px" }}>Days</th>
            <th>Currency</th>
            <th >Conv. Rate</th>
            <th style={{ textAlign: 'right' }}>Offer Price{offerUnit}</th>
            <th style={{ textAlign: 'right' }}>Total Offer Price</th>
            <th style={{ textAlign: 'right' }}>Offered INR{offerUnit}</th>
            <th style={{ }}>Invoicing Rate</th>
            <th style={{ textAlign: 'right' }}>Invoicing Price ({invoiceCurrency})</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((li, i) => {
            const d = daysOf(li);
            const autoRate = defaultPaymentRate(li, invoiceCurrency, currencyRates);
            // While a field is mid-edit (cleared to type a new value) paymentRate is
            // briefly '' — parseFloat('') is NaN, and letting that propagate would show
            // literal "NaN" in the OTHER (linked) rate/price fields instead of them just
            // going blank too. Treat "currently unparseable" as null everywhere instead,
            // so both sides of every linked pair clear together and re-sync the moment a
            // valid number is typed, rather than looking disconnected.
            const pRateParsed = parseFloat(li.paymentRate ?? autoRate);
            const pRate = isNaN(pRateParsed) ? null : pRateParsed;
            const pINR = pRate == null ? null : +(parseFloat(li.offered || 0) * pRate).toFixed(2);
            const isOverridden = pRate != null && Math.abs(pRate - autoRate) > 0.0001;
            return (
              <tr key={i}>
                {showJobColumn && <td>{li.jobNumber || '—'}</td>}
                <td>{li.inspectorName || '—'}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <span>{d}</span>
                    {(li.inspectionDates?.length > 0 || li.availableDates?.length > 0) && (
                      <button type="button" title={editable && li.availableDates?.length ? 'Select dates to invoice' : 'View inspection dates'} onClick={() => setDatesModalIndex(i)}
                        style={{ background: 'none', border: 'none', color: '#437cf0', cursor: 'pointer', padding: 0, display: 'flex' }}>
                        <FiCalendar size={12} />
                      </button>
                    )}
                  </div>
                </td>
                <td>{li.currencyOffered || 'INR'}</td>
                <td style={{ color: '#9ca3af' }}>{li.offerConvRate ?? 1}</td>
                <td style={{ textAlign: 'right' }}>{fmt(li.offered)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(totalOfferOf(li))}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(li.offeredINR)}</td>
                <td style={{ }}>
                  {editable ? (
                    <input
                      type="number"
                      step="0.0001"
                      value={li.paymentRate ?? autoRate}
                      onChange={e => onLineItemChange(i, { paymentRate: e.target.value })}
                      title={`Invoicing rate — converts offered price (${li.currencyOffered || 'INR'}) to invoice total (${invoiceCurrency}). Default: ${autoRate}`}
                      style={{
                        width: 80, padding: '2px 4px', fontSize: 12,
                        border: `1px solid ${isOverridden ? '#3b82f6' : '#d1d5db'}`,
                        borderRadius: 4,
                        background: isOverridden ? '#dbeafe' : '#f0f7ff',
                        fontWeight: isOverridden ? 700 : 400,
                      }}
                    />
                  ) : (
                    <span style={{ fontWeight: 600 }}>{pRate}</span>
                  )}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {fmt(pINR)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700, background: '#f1f3f6' }}>
            <td colSpan={showJobColumn ? 7 : 6}>Total</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalOfferedINR)}</td>
            <td></td>
            <td style={{ textAlign: 'right', color: '#1d4ed8' }}>{fmt(grandTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    {/* Inspection Dates Modal — triggered by the calendar icon in the Days column. When
        editable, every still-uninvoiced date is shown and can be toggled on/off to choose
        exactly which dates this invoice bills; unpicked dates stay open for a later invoice. */}
    {datesModalIndex != null && lineItems[datesModalIndex] && (() => {
      const li = lineItems[datesModalIndex];
      const selectedDates = li.inspectionDates || [];
      const selectedSet = new Set(selectedDates);
      const canSelect = editable && li.availableDates?.length > 0;
      const pool = canSelect ? [...new Set([...li.availableDates, ...selectedDates])].sort() : selectedDates;
      // Dates with no uploaded report can't be picked (null = older data with no report
      // info, so no restriction). One already on the invoice can still be removed.
      const reportedSet = Array.isArray(li.reportedDates) ? new Set(li.reportedDates) : null;
      const noReport = (dt) => !!reportedSet && !reportedSet.has(dt);
      const selectablePool = pool.filter(dt => !noReport(dt) || selectedSet.has(dt));
      const selectedWithoutReport = selectedDates.filter(noReport);
      const setDates = (dates) => {
        if (dates.length === 0) return; // a line item must bill at least one date
        onLineItemChange(datesModalIndex, lineItemDateUpdates(li, dates));
      };
      const toggle = (dt) => setDates(selectedSet.has(dt) ? selectedDates.filter(x => x !== dt) : [...selectedDates, dt]);
      return (
      <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setDatesModalIndex(null); }}>
        <div className="fin-modal" style={{ maxWidth: "45%" }}>
          <div className="fin-modal-header">
            <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center' , gap : 8 }}>
              <FiCalendar size={18} style={{ marginRight: 6 }} /> Inspection Dates - {li.inspectorName || '—'}
            </h3>
            <button className="fin-modal-close" onClick={() => setDatesModalIndex(null)}><FiX /></button>
          </div>
          <div className="fin-modal-body">
            {canSelect && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, fontSize: 12, color: '#4b5563' }}>
                <span>Click a date to include or exclude it. <b>{selectedDates.length}</b> of {pool.length} selected.</span>
                <button type="button" className="fin-btn fin-btn-outline" style={{ padding: '2px 10px', fontSize: 12 }}
                  disabled={selectedDates.length === selectablePool.length} onClick={() => setDates(selectablePool)}>
                  Select all
                </button>
              </div>
            )}
            {canSelect && reportedSet && (
              <div style={{ marginBottom: 10, fontSize: 12, color: '#6b7280' }}>
                Dates marked <span style={{ color: '#b91c1c', fontWeight: 600 }}>No report</span> have no uploaded report and can't be selected.
                {selectedWithoutReport.length > 0 && (
                  <div style={{ marginTop: 4, color: '#b45309' }}>
                    {selectedWithoutReport.length} selected date{selectedWithoutReport.length > 1 ? 's have' : ' has'} no report uploaded. Click to remove {selectedWithoutReport.length > 1 ? 'them' : 'it'}.
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {pool.map((dt) => {
                const on = selectedSet.has(dt);
                const missingReport = noReport(dt);
                const isLastSelected = on && selectedDates.length === 1;
                const blocked = isLastSelected || (!on && missingReport);
                return canSelect ? (
                  <button key={dt} type="button" onClick={() => toggle(dt)} disabled={blocked}
                    title={!on && missingReport ? 'Report not uploaded for this date — cannot be invoiced'
                      : isLastSelected ? 'At least one date must stay selected'
                      : (on ? 'Exclude from this invoice' : 'Include in this invoice')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: on ? (missingReport ? '#fffbeb' : '#eef2ff') : (missingReport ? '#f9fafb' : '#fff'),
                      color: on ? (missingReport ? '#b45309' : '#3730a3') : (missingReport ? '#d1d5db' : '#9ca3af'),
                      border: `1px solid ${on ? (missingReport ? '#fcd34d' : '#a5b4fc') : '#e5e7eb'}`, borderRadius: 12, padding: '3px 8px', fontSize: 12,
                      cursor: blocked ? 'not-allowed' : 'pointer', textDecoration: on ? 'none' : 'line-through',
                    }}>
                    {on && <FiCheck size={11} />}{fmtDDMMYY(dt)}
                    {missingReport && <span style={{ color: '#b91c1c', fontWeight: 600, fontSize: 10, textDecoration: 'none' }}>No report</span>}
                  </button>
                ) : (
                  <span key={dt} style={{ background: '#eef2ff', color: '#3730a3', borderRadius: 12, padding: '3px 8px', fontSize: 12 }}>
                    {fmtDDMMYY(dt)}
                  </span>
                );
              })}
            </div>
          </div>
          <div className="fin-modal-footer">
            <button className="fin-btn fin-btn-outline" onClick={() => setDatesModalIndex(null)}>{canSelect ? 'Done' : 'Close'}</button>
          </div>
        </div>
      </div>
      );
    })()}
    </>
  );
}

/* ── Receivables Tab ──────────────────────────────────────── */
const RECEIVABLE_STATUSES = ['Issued', 'Due', 'Overdue', 'Partially Received', 'Received', 'Cancelled'];

// Amount still owed on a receivable — remainingAmount comes from the server
// (decorateReceivableStatus); falls back to total for rows that predate it.
const receivableBalance = (r) => {
  if (r.receivableStatus === 'Received' || r.receivableStatus === 'Cancelled') return 0;
  return parseFloat(r.remainingAmount ?? r.total) || 0;
};

const receivableStatusStyle = (s) => {
  if (s === 'Received') return { background: '#d1fae5', color: '#065f46' };
  if (s === 'Partially Received') return { background: '#ede9fe', color: '#5b21b6' };
  if (s === 'Overdue')  return { background: '#fee2e2', color: '#991b1b' };
  if (s === 'Due')      return { background: '#fef3c7', color: '#92400e' };
  if (s === 'Cancelled') return { background: '#e5e7eb', color: '#374151' };
  return { background: '#dbeafe', color: '#1e40af' }; // Issued (default)
};

// Statuses a receivable can still be checked off for "Receive Payment" against —
// once Received or Cancelled there's nothing left to collect.
const RECEIVABLE_SELECTABLE_STATUSES = ['Issued', 'Due', 'Overdue', 'Partially Received'];

function ReceivablesTab({ rows, fmt, onStatusChange, toast, selectedIds, onToggleSelect, loading, invoiceNumberSortDir, onToggleInvoiceNumberSort, search, onSearchChange }) {
  const [updating, setUpdating] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  // Payment History popup — looked up live from `rows` so it reflects a payment
  // recorded while it's open (same pattern as Inspection Invoice).
  const [historyRowId, setHistoryRowId] = useState(null);
  const historyRow = historyRowId ? rows.find(r => r._id === historyRowId) : null;

  const handleChange = (row, newStatus) => {
    const oldLabel = row.receivableStatus || 'Issued';
    const newLabel = newStatus;
    toast.confirm(`Are you sure you want to change the status from "${oldLabel}" to "${newLabel}"?`, async () => {
      setUpdating(prev => ({ ...prev, [row._id]: true }));
      await onStatusChange(row, newStatus);
      setUpdating(prev => ({ ...prev, [row._id]: false }));
    }, undefined, 'Confirm Status Change');
  };

  // Same "status filter bar with counts" pattern as Inspection Invoice's Payable
  // statuses — click a status to filter the table to just that status, click again
  // (or the same one) to clear it.
  const statusCounts = RECEIVABLE_STATUSES.reduce((acc, s) => {
    acc[s] = rows.filter(r => (r.receivableStatus || 'Issued') === s).length;
    return acc;
  }, {});
  const filteredRows = statusFilter ? rows.filter(r => (r.receivableStatus || 'Issued') === statusFilter) : rows;
  // The "Received" view drops the Due Date column entirely — nothing is due on those.
  const showDueDate = statusFilter !== 'Received';
  const colCount = showDueDate ? 11 : 10;

  return (
    <>
      <div style={{ padding: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {RECEIVABLE_STATUSES.map(s => {
          const active = statusFilter === s;
          const style = receivableStatusStyle(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(active ? '' : s)}
              style={{
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                fontSize: 12,
                gap: 6,
                cursor: 'pointer',
                border: '1px solid #ccc',
                background: '#fff',
                fontWeight: active ? 600 : 400,
                transform: active ? 'translateY(-3px)' : 'translateY(0)',
                boxShadow: active ? '0 6px 16px rgba(0,0,0,0.25)' : 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
            >
              {s}
              <span style={{ ...style, borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>
                {statusCounts[s]}
              </span>
            </button>
          );
        })}
        <div className="fin-search-wrap" style={{ marginLeft: 'auto' }}>
          <FiSearch size={14} />
          <input className="fin-search-input" placeholder="Search invoice, job, customer…" value={search}
            onChange={e => onSearchChange(e.target.value)} />
        </div>
      </div>
      <div className="fin-table-wrap"  style={{ border: 0 }}>
      <table className="vendor-table">
        <thead>
          <tr>
            <th></th>
            <th>Job Number</th>
            <th onClick={onToggleInvoiceNumberSort} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
              Invoice Number{' '}
              {invoiceNumberSortDir === 'asc' ? <FiChevronUp size={13} /> : invoiceNumberSortDir === 'desc' ? <FiChevronDown size={13} /> : <span style={{ color: '#bbb' }}><FiChevronUp size={11} /></span>}
            </th>
            <th>Date</th>{showDueDate && <th>Due Date</th>}<th>Customer</th><th>Total</th><th>Received</th><th>Balance</th><th>Status</th><th>History</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={colCount} className="fin-loading">Loading…</td></tr>
          ) : filteredRows.length === 0 ? (
            <tr><td colSpan={colCount} className="fin-empty">{statusFilter ? `No "${statusFilter}" receivables` : 'No issued receivables'}</td></tr>
          ) : filteredRows.map(r => (
            <tr key={r._id}>
              <td>
                {RECEIVABLE_SELECTABLE_STATUSES.includes(r.receivableStatus || 'Issued') && (
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r._id)}
                    onChange={() => onToggleSelect(r._id)}
                    title="Select for payment"
                  />
                )}
              </td>
              <td><JobNumberCell row={r} /></td>
              <td>{r.invoiceNumber}</td>
              <td>{r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString('en-IN') : '—'}</td>
              {/* A fully received invoice has nothing left to fall due — no countdown. */}
              {showDueDate && <td>{r.receivableStatus === 'Received' ? '—' : <DueDateDaysLabel dueDate={r.dueDate} />}</td>}
              <td>{r.customerName || '—'}</td>
              <td>
                <span style={{ color: '#3730a3', fontWeight: 700, marginRight: 2 }}>
                  {currencySymbol(r.invoiceCurrency)}
                </span>
                {fmt(r.total)}
              </td>
              <td style={{ color: '#065f46' }} title={r.tdsAmount > 0 ? `Includes TDS ${currencySymbol(r.invoiceCurrency)} ${fmt(r.tdsAmount)}` : undefined}>
                {currencySymbol(r.invoiceCurrency)} {fmt(r.amountReceived || 0)}
                {r.tdsAmount > 0 && (
                  <div style={{ fontSize: 10, color: '#92400e' }}>TDS {currencySymbol(r.invoiceCurrency)} {fmt(r.tdsAmount)}</div>
                )}
              </td>
              <td style={receivableBalance(r) > 0 ? { color: r.isOverdue ? '#991b1b' : '#92400e', fontWeight: 600 } : { color: '#9ca3af' }}>
                {currencySymbol(r.invoiceCurrency)} {fmt(receivableBalance(r))}
              </td>
              <td>
                <select
                  value={r.receivableStatus || 'Issued'}
                  disabled={!!updating[r._id] || r.receivableStatus === 'Received' || r.receivableStatus === 'Cancelled'}
                  title={
                    r.receivableStatus === 'Received' ? 'Locked — payment already received'
                    : r.receivableStatus === 'Cancelled' ? 'Locked — receivable cancelled'
                    : 'Due/Overdue switch automatically based on the due date — only Cancel is a manual choice here'
                  }
                  onChange={e => handleChange(r, e.target.value)}
                  style={{
                    ...receivableStatusStyle(r.receivableStatus),
                    border: 'none', borderRadius: 4, padding: '3px 6px',
                    fontSize: 11, fontWeight: 600,
                    cursor: (r.receivableStatus === 'Received' || r.receivableStatus === 'Cancelled') ? 'not-allowed' : 'pointer',
                    opacity: updating[r._id] ? 0.5 : 1,
                  }}
                >
                  {/* Due/Overdue are computed automatically from the due date (see
                      effectiveReceivableStatus server-side) and Received is only reachable via
                      "Receive Payment" (captures amount/date/rate) — so the only manual action left
                      here is Cancel. Each row's own current status is always included too, so the
                      select always has a valid selected option to display. */}
                  {RECEIVABLE_STATUSES
                    .filter(s => s === r.receivableStatus || s === 'Cancelled')
                    .map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {r.receivableStatus === 'Partially Received' && r.isOverdue && (
                  <div style={{ fontSize: 10, color: '#991b1b', fontWeight: 600, marginTop: 2 }}>Overdue</div>
                )}
              </td>
              <td>
                {r.payments?.length > 0
                  ? <button type="button" title={`Payment History (${r.payments.length})`} onClick={() => setHistoryRowId(r._id)}
                      style={{ border: '1px solid #c7d2fe', borderRadius: 4, background: '#eef2ff', color: '#3730a3', padding: 4, display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 11 }}>
                      <FiClock size={14} /> {r.payments.length}
                    </button>
                  : <span title="No payment history yet" style={{ border: '1px solid #e5e7eb', borderRadius: 4, color: '#ccc', padding: 4, display: 'inline-flex' }}>
                      <FiClock size={14} />
                    </span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {historyRow && (
        <ReceivablePaymentHistoryModal row={historyRow} fmt={fmt} onClose={() => setHistoryRowId(null)} />
      )}
    </>
  );
}

/* ── Receivable Payment History popup ─────────────────────── */
function ReceivablePaymentHistoryModal({ row, fmt, onClose }) {
  const sym = currencySymbol(row.invoiceCurrency);
  const payments = row.payments || [];
  const totalINR = payments.reduce((s, p) => s + (parseFloat(p.amountINR) || 0), 0);
  const summaryItem = (label, value, color) => (
    <div style={{ flex: 1, minWidth: 140, border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
  );
  return (
    <div className="fin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="fin-modal" style={{ maxWidth: '85%' }}>
        <div className="fin-modal-header">
          <h3 className="fin-modal-title" style={{ display: 'flex', alignItems: 'center' }}>
            <FiClock size={18} style={{ marginRight: 6 }} /> Payment History - {row.invoiceNumber} ({row.customerName || '—'})
          </h3>
          <button className="fin-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="fin-modal-body">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            {summaryItem('Payments', payments.length, '#3730a3')}
            {summaryItem('Invoice Total', `${sym} ${fmt(row.total)}`, '#111827')}
            {summaryItem('Received (incl. TDS)', `${sym} ${fmt(row.amountReceived || 0)}`, '#065f46')}
            {summaryItem('TDS Deducted', `${sym} ${fmt(row.tdsAmount || 0)}`, '#92400e')}
            {summaryItem('Pending', `${sym} ${fmt(receivableBalance(row))}`, receivableBalance(row) > 0 ? '#991b1b' : '#9ca3af')}
            {summaryItem('Received in INR', `₹${fmt(totalINR)}`, '#111827')}
          </div>
          {payments.length === 0 ? (
            <p style={{ fontSize: 13, color: '#888' }}>No payments recorded yet.</p>
          ) : (
            <div className="fin-table-wrap">
              <table className="vendor-table">
                <thead>
                  <tr>
                    <th>Sr No.</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'right' }}>TDS</th>
                    <th style={{ textAlign: 'right' }}>Total Amount</th>
                    <th style={{ textAlign: 'right' }}>Rate</th>
                    <th style={{ textAlign: 'right' }}>Bank Charges</th>
                    <th style={{ textAlign: 'right' }}>Amount (INR)</th>
                    <th style={{ textAlign: 'right' }}>Received So Far</th>
                    <th style={{ textAlign: 'right' }}>Balance After</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={p._id || i}>
                      <td>{i + 1}</td>
                      <td>{p.date ? new Date(p.date).toLocaleDateString('en-IN') : '—'}</td>
                      <td>
                        <span style={{ ...(p.type === 'Full' ? receivableStatusStyle('Received') : receivableStatusStyle('Partially Received')), borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 600 }}>
                          {p.type || 'Partial'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>{sym} {fmt(p.amount)}</td>
                      <td style={{ textAlign: 'right' }}>{p.tdsAmount ? `${sym} ${fmt(p.tdsAmount)}` : '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{sym} {fmt((parseFloat(p.amount) || 0) + (parseFloat(p.tdsAmount) || 0))}</td>
                      <td style={{ textAlign: 'right' }}>{p.rate != null ? fmt(p.rate) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{p.bankCharges ? `${sym} ${fmt(p.bankCharges)}` : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{p.amountINR != null ? `₹${fmt(p.amountINR)}` : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{sym} {fmt(p.cumulativeAmountReceived)}</td>
                      <td style={{ textAlign: 'right' }}>{sym} {fmt(Math.max(0, (parseFloat(row.total) || 0) - (parseFloat(p.cumulativeAmountReceived) || 0)))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="fin-modal-footer">
          <button className="fin-btn fin-btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
